/**
 * jaiio-search-page — page de résultats du moteur maison : grille + facettes +
 * tri + chargement progressif + zéro résultat, sur les sections qui rendent
 * `[data-jaiio-search]` (main-collection-search, main-search-search), seules à
 * charger ce fichier.
 *
 * - Source de vérité de l'état = l'URL (params canoniques : marque=…&taille=S&taille=M
 *   &prix_min=…&sort=…&q=…&take=…). Les URLs legacy uff_ et usf_ sont traduites au boot.
 * - Panne API : un retry borné, grille conservée et reprise explicite.
 *
 * Dépend du socle `assets/jaiio-search.js` (layout, chargé avant dans l'ordre du
 * document, tous deux en `defer`) : état, émetteur, client d'API et rendu des prix
 * viennent de `window.jaiioSearch._core`. Ce fichier rend réelles les actions
 * d'écriture de l'API publique (setFilter, setSort…).
 *
 * Les custom elements Focal (product-form, toggle-button, quick-buy-drawer/popover,
 * loader-button, cart-notification, cart-count) s'upgradent automatiquement à
 * l'insertion du HTML (polyfill @ungap/custom-elements chargé par vendor.js pour Safari).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-jaiio-search]');
  var core = window.jaiioSearch && window.jaiioSearch._core;
  // Sans socle (pas de configuration rendue), rien à hydrater : la grille serveur reste.
  if (!root || !core) return;

  var config = core.config;
  var state = core.state;
  var results = core.results;
  var FACETS = core.FACETS;
  var FILTER_KEYS = core.FILTER_KEYS;
  var PAGE_SIZE = core.PAGE_SIZE;
  var emit = core.emit;
  var getFilters = core.getFilters;
  var getState = core.getState;
  var apiFetch = core.apiFetch;
  var esc = core.esc;
  var toNumber = core.toNumber;
  var fill = core.fill;
  var formatCount = core.formatCount;
  var normalize = core.normalize;
  var imageWithWidth = core.imageWithWidth;
  var dataLayerPush = core.dataLayerPush;
  var renderPrices = core.renderPrices;
  var renderAttributes = core.renderAttributes;
  var cardAttributesText = core.cardAttributesText;

  /* ──────────────────────────────────────────────────────────────────────────
   * Constantes de la page (tri, params legacy, swatches, pagination)
   * ────────────────────────────────────────────────────────────────────────── */

  var T_SORT = ((config.text && config.text.facets) || {}).sort || {};

  var SORTS = [
    { value: config.page_type === 'collection' ? '-created' : '-date', label: T_SORT.newest },
    { value: config.page_type === 'collection' ? 'created' : 'date', label: T_SORT.oldest },
    { value: 'price',  label: T_SORT.price_asc },
    { value: '-price', label: T_SORT.price_desc }
  ];
  var SORT_RELEVANCE_LABEL = T_SORT.relevance;
  // `date` reste la date de publication historique du moteur. Ne pas réinterpréter
  // une ancienne URL : la création Shopify utilise deux nouveaux tris explicites.
  var SUPPORTED_SORTS = ['-created', 'created', '-date', 'date', 'price', '-price', 'r'];

  // Table de traduction des params legacy USF (facetId base36 → clé canonique).
  var LEGACY_FACETS = {
    z3p48i: 'univers',
    '661xbv': 'categorie',
    jiqyf2: 'sous_categorie',
    c7f7gx: 'sous_sous_categorie',
    bt2y6s: 'marque',
    xu0mto: 'taille',
    '21dodu': 'pointure',
    qegquk: 'couleur',
    h9w6lb: 'prix',
    elcbdd: 'etat',
    t8dhoa: 'matiere',
    wfev9t: 'tags',
    eg719u: 'prix_doux'
  };
  var LEGACY_VALUE_SEPARATOR = '_usf_';

  // Swatches couleurs — copie de la config USF live (facette Couleurs, dump 2026-07-07).
  // Peut être surchargée par settings.color_swatch_config ("Nom: #hex" ou "Nom: fichier.png").
  var COLOR_SWATCHES = {
    'anthracite':  { color: '#636567' },
    'argent':      { image: 'https://cdn.shopify.com/s/files/1/0701/1493/5090/files/Jaiio_couleur_-_Argente.jpg' },
    'autre':       { image: 'https://cdn.shopify.com/s/files/1/0701/1493/5090/files/autres_coloris.png' },
    'beige':       { color: '#E3CAA7' },
    'blanc':       { color: '#FFFFFF' },
    'bleu':        { color: '#0C8BF8' },
    'bordeaux':    { color: '#8A332E' },
    'camel':       { color: '#C19A6B' },
    'doré':        { image: 'https://cdn.shopify.com/s/files/1/0701/1493/5090/files/Jaiio_couleur_-_Dore.jpg' },
    'ecru':        { color: '#FEFCE6' },
    'gris':        { color: '#9FA5AA' },
    'jaune':       { color: '#F6D606' },
    'kaki':        { color: '#596643' },
    'marine':      { color: '#000080' },
    'marron':      { color: '#754306' },
    'multicolore': { image: 'https://cdn.shopify.com/s/files/1/0701/1493/5090/files/multicolore.png' },
    'métallique':  { image: 'https://cdn.shopify.com/s/files/1/0701/1493/5090/files/Jaiio_couleur_-_Metallique.jpg' },
    'noir':        { color: '#000000' },
    'orange':      { color: '#F68A06' },
    'rose':        { color: '#FFB6C1' },
    'rouge':       { color: '#E30A07' },
    'turquoise':   { color: '#48D1CC' },
    'vert':        { color: '#228B22' },
    'violet':      { color: '#663399' }
  };

  // Ordre canonique de la facette Tailles (les valeurs inconnues passent après).
  var SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];

  // Même profondeur que l'index et l'API ; chaque appel reste limité à 60 pièces.
  var MAX_RESULT_ITEMS = 100000;
  var MAX_SKIP = MAX_RESULT_ITEMS - 1;
  var MAX_RESTORE_TAKE = MAX_RESULT_ITEMS;
  // Les pages HTML exposent 40 liens, les appels API restent des lots de 60.
  // `take` compte les cartes depuis le début de la page HTML, pas depuis le catalogue.
  var SSR_PAGE_SIZE = parseInt(root.getAttribute('data-jaiio-page-size'), 10) || 40;
  var SHOPIFY_SORTS = { '-created': 'created-descending', created: 'created-ascending', price: 'price-ascending', '-price': 'price-descending' };
  var VP_STORAGE_KEY = 'jaiioVP';
  var beforePushState = function () {};
  // Tailles cochées, gardées d'une page à l'autre (retour d'Edouard du 2026-10-08 :
  // changer de collection obligeait à ressaisir sa taille). Même stockage que le
  // choix d'univers (`jaiio_mode`) : c'est une propriété de la personne, pas de la
  // page. Oublié dès que plus aucune taille n'est cochée.
  var SIZES_STORAGE_KEY = 'jaiio:sizes';
  var SIZE_KEYS = ['taille', 'pointure'];

  /* ──────────────────────────────────────────────────────────────────────────
   * Utils de la page
   * ────────────────────────────────────────────────────────────────────────── */

  // Formatte un montant en respectant shop.money_format (ex. "{{amount_with_comma_separator}}€").
  // Toujours 2 décimales et pas d'espace avant le symbole (« 85,00€ ») — identique au
  // price_formatted renvoyé par le back (contrat v1.1 corrigé).
  function formatMoney(amount) {
    if (amount == null) return '';
    var format = config.money_format || '{{amount_with_comma_separator}}€';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = match ? match[1] : 'amount_with_comma_separator';
    var decimals = token.indexOf('no_decimals') !== -1 ? 0 : 2;
    var thousand = ',', decimal = '.';
    if (token.indexOf('with_comma_separator') !== -1) { thousand = '.'; decimal = ','; }
    if (token.indexOf('with_space_separator') !== -1) { thousand = ' '; decimal = ','; }
    var parts = Math.abs(amount).toFixed(decimals).split('.');
    var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
    var formatted = (amount < 0 ? '-' : '') + intPart + (parts[1] ? decimal + parts[1] : '');
    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted).replace(/<[^>]*>/g, '')
      .replace(/[\s\u00A0]+(€)/g, '$1').trim();
  }

  // srcset depuis une URL CDN Shopify (transformations à la volée via ?width=).
  function buildSrcset(url, widths) {
    if (!url) return '';
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return widths.map(function (w) { return url + sep + 'width=' + w + ' ' + w + 'w'; }).join(', ');
  }

  // Style d'un swatch couleur : config thème prioritaire, sinon map USF live.
  function swatchStyle(label) {
    var lower = normalize(label);
    var themeConfig = config.swatch_config || [];
    for (var i = 0; i < themeConfig.length; i++) {
      var parts = String(themeConfig[i]).split(':');
      if (parts.length < 2) continue;
      if (normalize(parts[0].trim()) !== lower) continue;
      var value = parts.slice(1).join(':').trim();
      if (value.indexOf('#') !== -1) return 'background-color: ' + value + ';';
      // Fichier du shop : pattern <fichier>_72x72.<ext> dans /files/ (cf. color-swatch-style.liquid)
      var base = (config.swatch_file_sample || '').split('multicolors_72x72.png')[0];
      var dot = value.lastIndexOf('.');
      if (base && dot !== -1) {
        return 'background-image: url(' + base + value.slice(0, dot) + '_72x72' + value.slice(dot) + ');';
      }
      return 'background-color: ' + esc(label).toLowerCase() + ';';
    }
    var known = COLOR_SWATCHES[String(label).toLowerCase()];
    if (known) {
      if (known.image) return 'background-image: url(' + imageWithWidth(known.image, 72) + ');';
      return 'background-color: ' + known.color + ';';
    }
    return 'background-color: ' + esc(label).toLowerCase() + ';';
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * État — source de vérité : l'URL (objets `state` / `results` du socle)
   * ────────────────────────────────────────────────────────────────────────── */

  var openFacets = {};  // état ouvert/replié des groupes, conservé entre re-rendus
  var facetSearchTerms = {}; // texte tapé dans la recherche interne (Créateurs)

  function parseUrl() {
    var params = new URLSearchParams(window.location.search);
    var legacySeen = false;
    var next = { q: null, sort: config.default_sort, filters: {}, prixMin: null, prixMax: null, take: PAGE_SIZE, page: 1 };

    // Un lien de pagination conserve l'ordre natif Shopify. Les liens du moteur
    // portent également `sort`, qui reste le contrat de l'API et prend priorité.
    Object.keys(SHOPIFY_SORTS).forEach(function (sort) {
      if (params.get('sort_by') === SHOPIFY_SORTS[sort]) next.sort = sort;
    });

    params.forEach(function (value, key) {
      if (key.indexOf('uff_') === 0) {
        legacySeen = true;
        var rest = key.slice(4);
        var idEnd = rest.indexOf('_');
        if (idEnd === -1) return;
        var canonical = LEGACY_FACETS[rest.slice(0, idEnd)];
        if (!canonical) return;
        if (canonical === 'prix') {
          // Format legacy ":min :max"
          var m = value.match(/:\s*(\d+\.?\d*)\s+:\s*(\d+\.?\d*)/);
          if (m) { next.prixMin = parseFloat(m[1]); next.prixMax = parseFloat(m[2]); }
          return;
        }
        value.split(LEGACY_VALUE_SEPARATOR).forEach(function (v) {
          if (v === '') return;
          if (canonical === 'prix_doux') v = v.replace(/\.0$/, '');
          (next.filters[canonical] = next.filters[canonical] || []).push(v);
        });
        return;
      }
      if (key === 'usf_sort') { legacySeen = true; next.sort = value; return; }
      if (key === 'usf_take') { legacySeen = true; next.take = parseInt(value, 10) || PAGE_SIZE; return; }
      if (key.indexOf('usf_') === 0) { legacySeen = true; return; }
      if (key === 'page') {
        var page = Number(value);
        if (/^[1-9]\d*$/.test(value) && Number.isSafeInteger(page)) next.page = page;
        else legacySeen = true;
        return;
      }

      if (key === 'q') { next.q = value || null; return; }
      if (key === 'sort') { next.sort = value; return; }
      if (key === 'take') { next.take = parseInt(value, 10) || PAGE_SIZE; return; }
      if (key === 'prix_min') { next.prixMin = toNumber(value); return; }
      if (key === 'prix_max') { next.prixMax = toNumber(value); return; }
      if (FILTER_KEYS.indexOf(key) !== -1) {
        (next.filters[key] = next.filters[key] || []).push(value);
      }
      // Autres params (view, utm…) : ignorés ici, préservés par writeUrl().
    });

    // Cas particulier /collections/vendors?q=X : q devient un filtre marque.
    if (config.page_type === 'collection' && config.collection === 'vendors' && next.q) {
      next.filters.marque = [next.q];
      next.q = null;
      legacySeen = true; // force un replaceState propre
    }

    if (SUPPORTED_SORTS.indexOf(next.sort) === -1) {
      next.sort = config.default_sort;
    }
    next.take = Math.max(PAGE_SIZE, Math.min(next.take, MAX_RESTORE_TAKE));

    state.q = next.q;
    state.sort = next.sort;
    state.filters = next.filters;
    state.prixMin = next.prixMin;
    state.prixMax = next.prixMax;
    state.take = next.take;
    state.page = config.page_type === 'collection' ? next.page : 1;
    return legacySeen;
  }

  // Construit la query string canonique de l'état courant (sans les params étrangers).
  function stateParams() {
    var params = new URLSearchParams();
    if (state.q) params.set('q', state.q);
    FILTER_KEYS.forEach(function (key) {
      (state.filters[key] || []).forEach(function (v) { params.append(key, v); });
    });
    if (state.prixMin != null) params.set('prix_min', String(state.prixMin));
    if (state.prixMax != null) params.set('prix_max', String(state.prixMax));
    if (state.sort && state.sort !== config.default_sort) {
      params.set('sort', state.sort);
      if (config.page_type === 'collection' && SHOPIFY_SORTS[state.sort]) params.set('sort_by', SHOPIFY_SORTS[state.sort]);
    }
    if (state.page > 1) params.set('page', String(state.page));
    if (state.take > PAGE_SIZE) params.set('take', String(state.take));
    return params;
  }

  function readRememberedSizes() {
    var raw = null;
    try { raw = JSON.parse(localStorage.getItem(SIZES_STORAGE_KEY) || 'null'); } catch (e) { return null; }
    if (!raw || typeof raw !== 'object') return null;
    var out = {};
    SIZE_KEYS.forEach(function (key) {
      var list = Array.isArray(raw[key]) ? raw[key].map(String).filter(Boolean) : [];
      if (list.length) out[key] = list;
    });
    return Object.keys(out).length ? out : null;
  }

  // Après chaque geste sur les filtres : les tailles cochées sont retenues, tout
  // décoché vaut « je ne filtre plus sur ma taille ». Pas de lecture de l'adresse
  // (lien partagé, retour arrière) : seul un choix fait sur la page compte.
  function rememberSizes() {
    var out = {};
    SIZE_KEYS.forEach(function (key) {
      if ((state.filters[key] || []).length) out[key] = state.filters[key].slice();
    });
    try {
      if (Object.keys(out).length) localStorage.setItem(SIZES_STORAGE_KEY, JSON.stringify(out));
      else localStorage.removeItem(SIZES_STORAGE_KEY);
    } catch (e) { /* stockage indisponible : les tailles restent dans l'adresse de la page */ }
  }

  // Au chargement, une adresse sans taille ni pointure reprend les tailles retenues.
  // Renvoie les groupes repris, à confirmer par le moteur (cf. dropUnofferedSizes).
  function applyRememberedSizes() {
    var urlHasSize = SIZE_KEYS.some(function (key) { return (state.filters[key] || []).length > 0; });
    var remembered = urlHasSize ? null : readRememberedSizes();
    if (!remembered) return null;
    Object.keys(remembered).forEach(function (key) { state.filters[key] = remembered[key].slice(); });
    return Object.keys(remembered);
  }

  function writeUrl(replace) {
    var url = new URL(window.location.href);
    var preserved = new URLSearchParams();
    // Préserve les params étrangers au moteur (view=search-v2, utm…, hors legacy).
    url.searchParams.forEach(function (value, key) {
      var ours = key === 'q' || key === 'sort' || key === 'sort_by' || key === 'take' ||
        key === 'prix_min' || key === 'prix_max' ||
        FILTER_KEYS.indexOf(key) !== -1 ||
        key.indexOf('uff_') === 0 || key.indexOf('usf_') === 0 || key === 'page';
      if (!ours && !preserved.has(key)) preserved.append(key, value);
    });
    var merged = stateParams();
    preserved.forEach(function (value, key) { merged.append(key, value); });
    var qs = merged.toString();
    var target = url.pathname + (qs ? '?' + qs : '');
    if (replace) history.replaceState(history.state, '', target);
    else {
      beforePushState(); // garde aussi la position de l'entrée quittée par un filtre/tri
      history.pushState({ jaiioSearch: true }, '', target);
    }
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * Paramètres d'API et repli
   * ────────────────────────────────────────────────────────────────────────── */

  function productParams(skip, take) {
    var params = new URLSearchParams();
    if (state.q) params.set('q', state.q);
    if (config.page_type === 'collection' && config.collection && config.collection !== 'vendors') {
      params.set('collection', config.collection);
    }
    FILTER_KEYS.forEach(function (key) {
      (state.filters[key] || []).forEach(function (v) { params.append(key, v); });
    });
    if (state.prixMin != null) params.set('prix_min', String(state.prixMin));
    if (state.prixMax != null) params.set('prix_max', String(state.prixMax));
    if (state.sort && state.sort !== 'r') params.set('sort', state.sort);
    params.set('skip', String(skip));
    params.set('take', String(take));
    return params;
  }

  function pageOffset() { return ((state.page || 1) - 1) * SSR_PAGE_SIZE; }

  /* ──────────────────────────────────────────────────────────────────────────
   * Rendu des cartes produit — markup STRICTEMENT calqué sur snippets/product-item.liquid.
   * ⚠️ `.product-item__image-wrapper > a.product-item__aspect-ratio[href="/products/…"]`
   * est CONTRACTUEL : Wishlist King s'y accroche pour injecter les cœurs.
   * ────────────────────────────────────────────────────────────────────────── */

  // Mention unique sous la photo (charte §07). Même priorité que
  // snippets/product-item.liquid : vendu, puis libellé marchand (tags `__label`,
  // les seuls que le back renvoie), puis nouveauté.
  function renderMention(item) {
    var s = config.settings;
    var text = '';
    var modifier = '';

    if (!item.available) {
      text = config.text.sold_out;
      modifier = ' mention--rose';
    }

    if (!text) {
      (item.tags || []).some(function (tag) {
        tag = String(tag);
        if (tag.indexOf('__label') === -1) return false;
        text = tag.split(':').pop();
        if (tag.indexOf('__label2') !== -1) modifier = ' mention--peche';
        return true;
      });
    }

    if (!text && item.is_new && s.label_highlight) {
      text = s.label_highlight;
      modifier = ' mention--corail';
    }

    // La ligne est rendue même vide : c'est elle qui aligne les prix d'une carte à l'autre.
    return '<p class="mention' + (text ? modifier : ' mention--none') + '">' + esc(text) + '</p>';
  }

  // ⚠️ Ce DOM doit rester identique à celui de snippets/product-item.liquid :
  // la même grille est rendue par le serveur puis hydratée ici. Le ratio est
  // fixé à 3/4 par la charte et posé en CSS ; il n'y a plus de padding-bottom
  // calculé ni de correction au chargement de l'image.
  function renderCard(item, index) {
    var s = config.settings;
    var eager = index < 4; // premières cartes au-dessus de la ligne de flottaison
    // Rendue même vide : parité avec le snippet Liquid, et rangée de hauteur
    // fixe — une ligne absente décalerait toute la méta d'une rangée.
    var brand = '<span class="product-item-meta__marque brand">' + esc(item.brand || '') + '</span>';

    return '<product-item class="product-item pc4' + (item.available ? '' : ' product-item--sold-out') + '"' +
      ' data-product-id="' + esc(item.id) + '">' +
      '<div class="product-item__image-wrapper">' +
      '<a href="' + esc(item.url) + '" class="product-item__aspect-ratio aspect-ratio gal"' +
      ' aria-label="' + esc(item.title) + '">' +
      (item.image_url
        ? '<img class="product-item__primary-image"' +
          ' loading="' + (eager ? 'eager' : 'lazy') + '"' + (eager ? ' fetchpriority="high"' : '') +
          ' sizes="' + esc(root.getAttribute('data-jaiio-card-sizes') || s.sizes_attribute || '(max-width: 740px) 200px, 280px') + '"' +
          ' src="' + esc(imageWithWidth(item.image_url, 600)) + '"' +
          ' srcset="' + esc(buildSrcset(item.image_url, [200, 300, 400, 500, 600, 800, 1000])) + '"' +
          ' alt="' + esc(item.image_alt || item.title) + '">'
        : '') +
      '</a>' +
      '<div class="product-item__media-nav dots" data-card-media-nav hidden></div>' +
      '</div>' +
      renderMention(item) +
      '<div class="product-item__info meta">' +
      '<div class="product-item-meta">' +
      brand +
      '<h2 class="product-item-meta__heading"><a href="' + esc(item.url) + '" class="product-item-meta__title title">' +
      esc(item.title) + '</a></h2>' +
      renderAttributes(item) +
      renderPrices(item) +
      '</div>' +
      '</div>' +
      '</product-item>';
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * Grille + facettes (la racine [data-jaiio-search] est garantie plus haut)
   * ────────────────────────────────────────────────────────────────────────── */

  initSearchPage();

  // Le contrat API versionné fournit la ligne Liquid complète. L'ancienne API
  // garde le secours SSR/Section Rendering pendant le déploiement progressif.
  function initCardAttributes(grid) {
    var cache = new Map();
    var apiValues = new Map();
    var pending = new Set();
    var queue = [];
    var active = 0;
    var observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { if (entry.isIntersecting) enqueue(entry.target); });
    }, { rootMargin: '200px' }) : null;

    grid.querySelectorAll('.pc4[data-product-id]').forEach(function (card) {
      var attrs = card.querySelector('.attrs');
      if (attrs) cache.set(card.dataset.productId, attrs.textContent.trim());
    });

    function apply(card, text) {
      var attrs = card.querySelector('.attrs');
      if (attrs && attrs.textContent !== text) attrs.textContent = text;
      if (card.dataset.cardAttributesReady !== 'true') card.dataset.cardAttributesReady = 'true';
      delete card.dataset.cardAttributesError;
      if (observer) observer.unobserve(card);
    }

    function enqueue(card) {
      var id = card.dataset.productId;
      if (cache.has(id)) { apply(card, cache.get(id)); return; }
      if (pending.has(id) || !card.isConnected) return;
      pending.add(id);
      queue.push(card);
      drain();
    }

    function drain() {
      while (active < 4 && queue.length) {
        var card = queue.shift();
        if (!card.isConnected) { pending.delete(card.dataset.productId); continue; }
        active++;
        load(card).finally(function () { active--; drain(); });
      }
    }

    async function load(card) {
      var id = card.dataset.productId;
      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); }, 8000);
      try {
        var link = card.querySelector('.product-item__aspect-ratio');
        var url = new URL(link.href);
        if (url.origin !== location.origin || !/^\/products\/[^/]+$/.test(url.pathname)) throw new Error('invalid_product_url');
        url.searchParams.set('section_id', 'product-card-attributes');
        var response = await fetch(url.pathname + url.search, { signal: controller.signal });
        if (!response.ok) throw new Error('http_' + response.status);
        var doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        var payload = doc.querySelector('[data-card-attributes-json]');
        var data = payload && JSON.parse(payload.textContent);
        if (!data || String(data.id) !== id || typeof data.attributes !== 'string') throw new Error('invalid_card_attributes');
        if (!apiValues.has(id)) cache.set(id, data.attributes);
        grid.querySelectorAll('.pc4[data-product-id]').forEach(function (current) {
          if (current.dataset.productId === id) apply(current, cache.get(id));
        });
      } catch (error) {
        if (apiValues.has(id)) return;
        // Taille et état déjà rendus restent lisibles. Une nouvelle entrée dans
        // l'écran pourra réessayer ; l'échec est observable sans bloquer la grille.
        card.dataset.cardAttributesError = error.message;
        emit('card:attributes:error', { id: id, reason: error.message });
      } finally {
        clearTimeout(timer);
        pending.delete(id);
      }
    }

    return function refresh(items, fromLiquid) {
      if (fromLiquid) grid.querySelectorAll('.pc4[data-product-id]').forEach(function (card) {
        var attrs = card.querySelector('.attrs');
        if (attrs) { cache.set(card.dataset.productId, attrs.textContent.trim()); apiValues.set(card.dataset.productId, attrs.textContent.trim()); }
      });
      (items || []).forEach(function (item) {
        var text = cardAttributesText(item);
        if (text !== null) {
          var id = String(item.id);
          apiValues.set(id, text);
          cache.set(id, text);
        }
      });
      if (observer) observer.disconnect();
      grid.querySelectorAll('.pc4[data-product-id]').forEach(function (card) {
        var id = card.dataset.productId;
        var attrs = card.querySelector('.attrs');
        if (!cache.has(id) && card.dataset.cardAttributesReady === 'true' && attrs) cache.set(id, attrs.textContent.trim());
        if (cache.has(id)) apply(card, cache.get(id));
        else if (observer) observer.observe(card);
        else enqueue(card);
      });
    };
  }

  function initSearchPage() {
    var els = {
      grid: root.querySelector('[data-jaiio-grid]'),
      skeleton: root.querySelector('[data-jaiio-skeleton]'),
      empty: root.querySelector('[data-jaiio-empty]'),
      sentinel: root.querySelector('[data-jaiio-sentinel]'),
      pag: root.querySelector('[data-jaiio-pag]'),
      loadMoreButton: root.querySelector('[data-jaiio-load-more]'),
      facets: root.querySelector('[data-jaiio-facets]'),
      facetsAside: root.querySelector('[data-jaiio-facets-aside]'),
      facetsPanel: root.querySelector('[data-jaiio-facets-panel]'),
      activeFilters: root.querySelector('[data-jaiio-active-filters]'),
      sortChoices: root.querySelector('[data-jaiio-sort-choices]'),
      sortLabel: root.querySelector('[data-jaiio-sort-label]'),
      sortButton: root.querySelector('[data-jaiio-sort-button]'),
      sortPopover: root.querySelector('[data-jaiio-sort-popover]'),
      viewItems: root.querySelector('[data-jaiio-view-items]'),
      activeCounts: root.querySelectorAll('[data-jaiio-active-count]'),
      // En-tête de résultats (`.rs-head`) : hors de [data-jaiio-search], comme la facet-bar.
      rsTitle: document.querySelector('[data-rs-title]'),
      rsContext: document.querySelector('[data-rs-context]'),
      rsBrand: document.querySelector('[data-rs-brand]'),
      // Zéro résultat de `/search` (T055). Absents des collections, qui gardent
      // leur `.cempty` : c'est leur présence qui choisit l'état vide.
      rsPage: document.querySelector('[data-rs-page]'),
      rsTop: document.querySelector('[data-rs-top]'),
      zeroOnly: document.querySelectorAll('[data-rs-zero-only]'),
      zeroTry: document.querySelector('[data-rs-zero-try]'),
      zeroList: document.querySelector('[data-rs-zero-list]'),
      zeroAll: document.querySelector('[data-rs-zero-all]'),
      zeroSec: document.querySelector('[data-rs-zero-sec]'),
      zeroSecTitle: document.querySelector('[data-rs-zero-sec-title]'),
      zeroSecLink: document.querySelector('[data-rs-zero-sec-link]'),
      zeroGrid: document.querySelector('[data-rs-zero-grid]'),
      unavailable: root.querySelector('[data-jaiio-unavailable]'),
      pageUnavailable: root.querySelector('[data-jaiio-page-unavailable]'),
      ssrPagination: root.querySelector('[data-jaiio-ssr-pagination]')
    };

    var refreshCardAttributes = initCardAttributes(els.grid);
    var initialSsrPagination = els.ssrPagination && els.ssrPagination.innerHTML;
    var currentFetch = 0; // jeton anti-course
    var resultsController = null;
    var nativeGridReady = false;
    var nativeNextPage = 1;
    var nativeTotal = toNumber(root.getAttribute('data-jaiio-collection-total'));
    var nativeMoreFailed = false;
    var cancelViewportRestore = function () {};

    var returnViewport = readViewportState();
    var legacySeen = parseUrl();
    // Un retour reprend sa sélection exacte, même si les tailles mémorisées ont
    // changé depuis une autre collection.
    var injectedSizes = returnViewport ? null : applyRememberedSizes();
    if (injectedSizes) state.page = 1;
    // Normalise l'URL (params legacy → canoniques) et y pose les tailles reprises :
    // l'adresse reste la source de vérité, partageable et relue au retour arrière.
    if (legacySeen || injectedSizes) writeUrl(true);
    if (returnViewport) state.take = Math.max(state.take, returnViewport.take || PAGE_SIZE);
    var renderedUrl = viewportUrl();
    var requestedUrl = renderedUrl;
    // Ce statut vient de la page HTML demandée, même lorsque son tri ou ses
    // filtres ne correspondent pas au SSR. Un changement de critères quitte
    // cette URL et repart à la page 1 : il peut alors demander de vrais résultats.
    var invalidSsrUrl = root.getAttribute('data-jaiio-page-valid') === 'false' && state.page > 1 ? renderedUrl : null;
    beforePushState = saveViewportState;

    // Grille rendue serveur (cf. main-collection-search.liquid) : tant qu'elle est à
    // l'écran, on ne la remplace que par un vrai résultat moteur, jamais par du vide.
    var ssrActive = !!(els.grid && els.grid.children.length);
    var cardPrimaryImages = new WeakMap();
    var cardMetadataMarkup = new WeakMap();
    Array.from(els.grid.children).forEach(function (card) {
      var image = card.querySelector('.product-item__primary-image');
      cardPrimaryImages.set(card, imageIdentity(image && image.getAttribute('src')));
    });
    // Une page et un tri Shopify explicites ont aussi un vrai rendu serveur.
    // Garder ce lot pendant une panne, sans jamais présenter les cartes non
    // filtrées comme le résultat d'une sélection propre au moteur.
    var nativeSort = config.collection_sort_by || new URLSearchParams(location.search).get('sort_by') || SHOPIFY_SORTS[config.default_sort];
    var pristineUrl = config.page_type === 'collection' && !legacySeen && !state.q &&
      state.prixMin == null && state.prixMax == null &&
      FILTER_KEYS.every(function (key) { return !(state.filters[key] || []).length; }) &&
      !!SHOPIFY_SORTS[state.sort] && SHOPIFY_SORTS[state.sort] === nativeSort;
    var ssrUrl = pristineUrl ? viewportUrl() : null;
    var ssrTotal = toNumber(config.collection_total);
    // La barre externe se monte avant la première réponse API. Son zéro initial
    // n'est pas un résultat : conserver le nombre déjà fourni par Shopify pour
    // le lot compatible, y compris si l'API ne peut pas prendre le relais.
    if (pristineUrl && ssrTotal != null && ssrTotal >= 0) results.total = ssrTotal;

    renderSortChoices();

    function scrollToResults() {
      // Pas de saut de page tant qu'un panneau ou une feuille de facettes est
      // ouvert : la visiteuse est en train de choisir, et le déplacement faisait
      // refermer le panneau par l'écouteur de scroll de la barre — elle perdait
      // sa place et, au clavier, son focus.
      if (document.querySelector('[data-panel]:not([hidden]), .facet-sheet.is-open')) return;
      var target = root.querySelector('#facet-main') || root;
      var header = document.querySelector('store-header');
      var facetBar = document.querySelector('facet-bar-engine');
      var headerHeight = header ? header.getBoundingClientRect().height : 0;
      var facetBarHeight = facetBar ? facetBar.getBoundingClientRect().height : 0;
      var top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - facetBarHeight - 12;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }

    /* ── Fetch + rendu ─────────────────────────────────────────────────── */

    function showSkeleton(show) {
      if (els.skeleton) els.skeleton.hidden = !show;
    }

    function firstPageUrl() {
      var url = new URL(location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('take');
      return url.pathname + url.search;
    }

    function setPageUnavailable(show) {
      if (els.pageUnavailable) els.pageUnavailable.hidden = !show;
      root.querySelectorAll('[data-jaiio-first-page]').forEach(function (link) { link.href = firstPageUrl(); });
    }

    function waitForRetry(delay, signal) {
      return new Promise(function (resolve, reject) {
        var timer;
        function cleanup() { clearTimeout(timer); signal.removeEventListener('abort', abort); }
        function abort() { cleanup(); reject(new DOMException('Requête remplacée', 'AbortError')); }
        if (signal.aborted) { abort(); return; }
        signal.addEventListener('abort', abort, { once: true });
        timer = setTimeout(function () { cleanup(); resolve(); }, delay);
      });
    }

    // Les égalités de prix n'ont pas le même départage Shopify/Meili. Une
    // collection sans critères garde donc Shopify comme autorité pour TOUS ses
    // lots prix, y compris après changement de tri et au retour d'une fiche.
    function usesShopifyPrice() {
      return config.page_type === 'collection' && (state.sort === 'price' || state.sort === '-price') &&
        !state.q && state.prixMin == null && state.prixMax == null &&
        FILTER_KEYS.every(function (key) { return !(state.filters[key] || []).length; });
    }

    function nativeHasMore() {
      return nativeGridReady && nativeNextPage <= 625 && (nativeNextPage - 1) * SSR_PAGE_SIZE < nativeTotal;
    }

    function nativeItems(cards) {
      return cards.map(function (card) {
        var link = card.querySelector('.product-item__aspect-ratio');
        var title = card.querySelector('.product-item-meta__title');
        return { id: card.dataset.productId, url: link && link.getAttribute('href'), title: title && title.textContent };
      });
    }

    function nativePageUrl(page) {
      var sectionId = root.getAttribute('data-jaiio-section-id');
      if (!sectionId) throw new Error('missing_collection_section');
      var url = new URL(location.href);
      ['q', 'sort', 'take', 'prix_min', 'prix_max'].concat(FILTER_KEYS).forEach(function (key) { url.searchParams.delete(key); });
      url.searchParams.set('sort_by', SHOPIFY_SORTS[state.sort]);
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', sectionId);
      return url.pathname + url.search;
    }

    function fetchNativePage(page, signal) {
      var expectedSort = SHOPIFY_SORTS[state.sort];
      var controller = new AbortController();
      function abort() { controller.abort(); }
      if (signal.aborted) abort();
      else signal.addEventListener('abort', abort, { once: true });
      var timer = setTimeout(abort, 10000);
      return Promise.resolve().then(function () {
        return fetch(nativePageUrl(page), { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
      }).then(function (response) {
        if (!response.ok) {
          var error = new Error('HTTP ' + response.status);
          error.status = response.status;
          error.retryAfter = response.headers && response.headers.get('Retry-After');
          throw error;
        }
        return response.text();
      }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = doc.querySelector('[data-jaiio-search]');
        if (!section || section.getAttribute('data-jaiio-section-id') !== root.getAttribute('data-jaiio-section-id')) throw new Error('invalid_collection_section');
        if (section.getAttribute('data-jaiio-page') !== String(page) || section.getAttribute('data-jaiio-sort') !== expectedSort) throw new Error('invalid_collection_order');
        var total = toNumber(section.getAttribute('data-jaiio-collection-total'));
        var grid = section.querySelector('[data-jaiio-grid]');
        if (!grid || total == null || total < 0 || grid.children.length > SSR_PAGE_SIZE) throw new Error('invalid_collection_page');
        return { cards: Array.from(grid.children), total: total, page: page,
          valid: section.getAttribute('data-jaiio-page-valid') !== 'false', pager: section.querySelector('[data-jaiio-ssr-pagination]') };
      }).finally(function () {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
      });
    }

    function applyNativePage(chunk, append) {
      if (append && !chunk.valid) {
        // Le catalogue a rétréci entre deux lots. L'URL de départ reste valide :
        // garder ses cartes et terminer le parcours, sans la déclarer hors limites.
        nativeTotal = chunk.total;
        results.total = nativeTotal;
        nativeNextPage = chunk.page;
        els.grid.hidden = false;
        showSkeleton(false);
        setPageUnavailable(false);
        setUnavailable(false);
        if (els.ssrPagination) els.ssrPagination.querySelectorAll('a[href]').forEach(function (link) {
          var page = Number(new URL(link.href, location.href).searchParams.get('page')) || 1;
          if ((page - 1) * SSR_PAGE_SIZE >= nativeTotal && page > 1) link.remove();
        });
        updatePagination();
        updateCounters();
        emit('change', getState());
        return;
      }
      var focused = els.grid.contains(document.activeElement) ? document.activeElement : null;
      var previous = new Map();
      Array.from(els.grid.children).forEach(function (card) { previous.set(card.dataset.productId, card); });
      var seen = new Set(append ? results.items.map(function (item) { return String(item.id); }) : []);
      var cards = [];
      chunk.cards.forEach(function (incoming) {
        var id = incoming.dataset.productId;
        if (!id || seen.has(id)) return;
        seen.add(id);
        var card = previous.get(id);
        var image = incoming.querySelector('.product-item__primary-image');
        var primaryIdentity = incoming.ownerDocument === document && cardPrimaryImages.has(incoming) ?
          cardPrimaryImages.get(incoming) : imageIdentity(image && image.getAttribute('src'));
        if (!card || cardPrimaryImages.get(card) !== primaryIdentity) {
          card = incoming.ownerDocument === document ? incoming : document.importNode(incoming, true);
        } else if (card !== incoming) {
          var info = card.querySelector('.product-item__info');
          var nextInfo = incoming.querySelector('.product-item__info');
          if (info && nextInfo && !info.isEqualNode(nextInfo)) info.replaceWith(document.importNode(nextInfo, true));
          var mention = card.querySelector('.mention');
          var nextMention = incoming.querySelector('.mention');
          if (mention && nextMention && !mention.isEqualNode(nextMention)) mention.replaceWith(document.importNode(nextMention, true));
          cardMetadataMarkup.delete(card);
        }
        cardPrimaryImages.set(card, primaryIdentity);
        var index = (append ? results.items.length : 0) + cards.length;
        prioritizeRetainedCard(card, index);
        if (index >= 4 && !previous.has(id)) {
          var primary = card.querySelector('.product-item__primary-image');
          if (primary) { primary.setAttribute('loading', 'lazy'); primary.removeAttribute('fetchpriority'); }
        }
        cards.push(card);
      });
      if (!append) {
        var wanted = new Set(cards);
        Array.from(els.grid.children).forEach(function (card) { if (!wanted.has(card)) card.remove(); });
      }
      var fragment = document.createDocumentFragment();
      if (append) cards.forEach(function (card) { fragment.appendChild(card); });
      else cards.forEach(function (card, index) { if (els.grid.children[index] !== card) els.grid.insertBefore(card, els.grid.children[index] || null); });
      if (append) els.grid.appendChild(fragment);
      if (focused && focused.isConnected && document.activeElement !== focused) focused.focus({ preventScroll: true });
      results.items = append ? results.items.concat(nativeItems(cards)) : nativeItems(cards);
      nativeTotal = chunk.total;
      results.total = nativeTotal;
      nativeNextPage = chunk.page + 1;
      nativeGridReady = true;
      ssrActive = false;
      nativeMoreFailed = false;
      renderedUrl = viewportUrl();
      els.grid.hidden = false;
      showSkeleton(false);
      setEmpty(!results.items.length && !nativeTotal);
      setUnavailable(false);
      setPageUnavailable(!chunk.valid);
      // Le pager du dernier lot réussi reste une sortie de secours au réseau.
      if (els.ssrPagination && chunk.pager) {
        els.ssrPagination.innerHTML = chunk.pager.innerHTML;
        els.ssrPagination.querySelectorAll('a[href]').forEach(function (link) {
          var url = new URL(link.href, location.href);
          url.searchParams.delete('section_id');
          link.href = url.pathname + url.search;
        });
      }
      root.removeAttribute('data-jaiio-hydrated');
      if (els.ssrPagination) els.ssrPagination.hidden = chunk.valid;
      if (!chunk.valid) els.grid.hidden = true;
      // Le HTML Shopify contient déjà les attributs définitifs : pas de N+1.
      cards.forEach(function (card) { card.dataset.cardAttributesReady = 'true'; });
      refreshCardAttributes([], true);
      updatePagination();
      updateCounters();
    }

    function loadNativeFresh(options, token, signal) {
      var first = state.page || 1;
      var pages = state.take > PAGE_SIZE ? Math.ceil(state.take / SSR_PAGE_SIZE) : 1;
      var resumeNative = nativeGridReady && renderedUrl === viewportUrl();
      var reusable = els.grid.children.length && ((ssrActive && ssrUrl === viewportUrl()) || resumeNative);
      var firstLoad = reusable ? Promise.resolve({ cards: Array.from(els.grid.children), total: nativeTotal == null ? ssrTotal : nativeTotal,
        page: resumeNative ? nativeNextPage - 1 : first, valid: true, pager: null }) : fetchNativePage(first, signal);
      // Les facettes ne décident jamais du classement ni du total de Shopify.
      apiFetch('/search/products', productParams(0, 1), { signal: signal }).then(function (data) {
        if (token !== currentFetch) return;
        var meta = Object.assign({}, data, { total: nativeTotal == null ? data.total : nativeTotal });
        applyResultMeta(meta);
      }).catch(function () { /* la grille et sa pagination Shopify restent utilisables */ });
      function appendNext() {
        if (token !== currentFetch || nativeNextPage >= first + pages || !nativeHasMore()) return Promise.resolve();
        var page = nativeNextPage;
        return fetchNativePage(page, signal).catch(function (error) {
          if (error.status !== 429) throw error;
          var seconds = toNumber(error.retryAfter);
          return waitForRetry(seconds != null && seconds >= 0 ? seconds * 1000 : 2000, signal).then(function () {
            return fetchNativePage(page, signal).catch(function (retryError) { retryError.restorePageRetriedAfter429 = true; throw retryError; });
          });
        }).then(function (chunk) {
          if (token !== currentFetch) return;
          applyNativePage(chunk, true);
          return appendNext();
        });
      }
      return firstLoad.then(function (chunk) {
        if (token !== currentFetch) return;
        applyNativePage(chunk, false);
        return appendNext();
      }).then(function () {
        if (token !== currentFetch) return;
        results.loading = false;
        state.take = (nativeNextPage - first) * SSR_PAGE_SIZE;
        emit('change', getState());
        emit('results', { total: results.total, items: results.items.length, q: state.q });
        if (options.onDone && !(els.pageUnavailable && !els.pageUnavailable.hidden)) options.onDone();
      }).catch(function (error) {
        if (token !== currentFetch) return;
        results.loading = false;
        handleFreshError(error, options, token);
        if (nativeGridReady && renderedUrl === viewportUrl()) {
          root.removeAttribute('data-jaiio-hydrated');
          if (els.ssrPagination) els.ssrPagination.hidden = false;
        }
      });
    }

    // Requête "reset" : nouvelle recherche/nouveaux filtres — repart de zéro.
    function loadFresh(options) {
      options = options || {};
      var token = ++currentFetch;
      if (resultsController) resultsController.abort();
      resultsController = new AbortController();
      var signal = resultsController.signal;
      cancelViewportRestore();
      requestedUrl = viewportUrl();
      results.loading = true;
      loadingMore = false; // un load-more en vol est invalidé par le jeton — on libère le verrou
      setLoadMoreBusy(false);
      setPageUnavailable(false);
      nativeMoreFailed = false;
      if (!options.silent) {
        // Garder les nœuds permet de récupérer d'une panne sans recréer les
        // photos, les contrôleurs de galerie et les cœurs déjà initialisés.
        els.grid.hidden = true;
        showSkeleton(true);
        setEmpty(false);
        setUnavailable(false);
        if (els.pag) els.pag.hidden = true;
        if (els.ssrPagination) els.ssrPagination.hidden = true;
      }
      var offset = pageOffset();
      var take = Math.min(state.take, Math.max(0, MAX_RESTORE_TAKE - offset));
      // Une page HTML hors limites reste un état explicite, sans demander un
      // skip invalide à l'API ni faire réapparaître les produits de la page 1.
      if (!take || invalidSsrUrl === viewportUrl()) {
        results.loading = false;
        showSkeleton(false);
        if (invalidSsrUrl === viewportUrl() && els.ssrPagination) {
          els.ssrPagination.innerHTML = initialSsrPagination;
          setPageUnavailable(false);
        }
        if (els.ssrPagination) els.ssrPagination.hidden = false;
        return Promise.resolve();
      }

      if (usesShopifyPrice()) return loadNativeFresh(options, token, signal);
      nativeGridReady = false;

      // Charge séquentiellement jusqu'à `take` produits (pages de 60) —
      // nécessaire pour la restauration du scroll au retour navigateur.
      var loaded = [];
      function fetchPage(skip, retriedAfter429) {
        return apiFetch('/search/products', productParams(offset + skip, Math.min(PAGE_SIZE, take - skip)), { signal: signal })
          .catch(function (e) {
            if (!e || e.status !== 429 || skip === 0) throw e;
            if (retriedAfter429) {
              // Ne pas recommencer toute la reconstruction après ce retry local.
              e.restorePageRetriedAfter429 = true;
              throw e;
            }
            var retryAfter = toNumber(e.retryAfter);
            var delay = retryAfter != null && retryAfter >= 0 ? retryAfter * 1000 : 2000;
            return waitForRetry(delay, signal).then(function () {
              if (token !== currentFetch) return null;
              return fetchPage(skip, true);
            });
          });
      }
      function loadPage(skip) {
        return fetchPage(skip, false)
          .then(function (data) {
            if (token !== currentFetch) return null;
            if (skip === 0 && dropUnofferedSizes(data)) { loadFresh(options); return null; }
            if (skip === 0) applyResultMeta(data);
            if (skip === 0 && state.page > 1 && data.total > 0 && offset >= data.total) {
              results.items = [];
              renderedUrl = viewportUrl();
              els.grid.hidden = true;
              showSkeleton(false);
              setEmpty(false);
              setUnavailable(false);
              setPageUnavailable(true);
              if (els.ssrPagination) els.ssrPagination.hidden = true;
              return null;
            }
            loaded = loaded.concat(data.items || []);
            if (skip === 0) {
              results.items = loaded.slice();
              renderedUrl = viewportUrl();
              // Le raccourci marque lit `brand_url` sur les pièces : il attend
              // qu'elles soient là, `applyResultMeta` passe avant.
              updateBrandShortcut(config.text.results || {});
              renderGrid(false);
              showSkeleton(false);
            } else {
              results.items = loaded.slice();
              appendGridFrom(skip);
            }
            var next = skip + PAGE_SIZE;
            if (next < take && loaded.length >= next && offset + next < results.total && offset + next <= MAX_SKIP) {
              return loadPage(next);
            }
            return null;
          });
      }

      return loadPage(0).then(function () {
        if (token !== currentFetch) return;
        results.loading = false;
        state.take = Math.max(PAGE_SIZE, results.items.length);
        emit('results', { total: results.total, items: results.items.length, q: state.q });
        if (options.onDone && !(els.pageUnavailable && !els.pageUnavailable.hidden)) options.onDone();
      }).catch(function (e) {
        if (token !== currentFetch) return;
        results.loading = false;
        handleFreshError(e, options, token);
      });
    }

    // Ré-affiche l'état courant (résultats déjà en mémoire) après un échec non destructif.
    function restorePreviousResults() {
      showSkeleton(false);
      els.grid.hidden = false;
      // Une ancienne sélection ne doit pas passer pour le résultat des nouveaux
      // critères. Garder ses nœuds pour le retour, sans les présenter comme valides.
      if (renderedUrl !== viewportUrl()) {
        els.grid.hidden = true;
        setEmpty(false);
        setUnavailable(true);
        return;
      }
      // Rien en mémoire et la grille serveur est encore là : on la laisse plutôt que
      // d'afficher un empty-state trompeur (4xx/429 au premier chargement).
      if (keepSsrGrid()) return;
      if (usesShopifyPrice() && nativeGridReady) { updatePagination(); return; }
      renderGrid(false);
      // `/search` : une requête rejetée n'est pas un zéro résultat (T055). Sans
      // pièce à montrer, on dit que le moteur n'a pas répondu, jamais « aucune pièce ».
      if (els.unavailable) {
        setEmpty(false);
        setUnavailable(!results.items.length);
        return;
      }
      setEmpty(!results.items.length);
    }

    // Bandeau discret, auto-effacé — utilisé pour le throttling (429).
    function showNotice(message) {
      var existing = root.querySelector('[data-jaiio-notice]');
      if (existing) existing.remove();
      els.grid.insertAdjacentHTML('beforebegin',
        '<p class="jaiio-notice text--small text--subdued" data-jaiio-notice role="status">' + esc(message) + '</p>');
      setTimeout(function () {
        var notice = root.querySelector('[data-jaiio-notice]');
        if (notice) notice.remove();
      }, 6000);
    }

    // Politique d'erreur de loadFresh :
    // - 429 : UN retry après Retry-After (défaut 2 s), sinon état conservé + message
    //   discret — JAMAIS de redirection ;
    // - 400 (et autres 4xx) : console.warn + état conservé ;
    // - timeout/réseau/5xx : un retry, puis reprise explicite sans navigation.
    function handleFreshError(e, options, token) {
      var status = e && e.status;
      if (status === 429) {
        if (!options.retriedAfter429 && !e.restorePageRetriedAfter429) {
          var retryAfter = toNumber(e.retryAfter);
          var delay = retryAfter != null && retryAfter >= 0 ? retryAfter * 1000 : 2000;
          waitForRetry(delay, resultsController.signal).then(function () {
            if (token !== currentFetch) return; // une autre requête a repris la main
            loadFresh({ silent: true, onDone: options.onDone, retriedAfter429: true,
              retriedTransient: options.retriedTransient });
          }).catch(function () { /* annulé par de nouveaux critères */ });
        } else {
          console.warn('[jaiio-search] API throttlée (429) après retry — état conservé', e);
          restorePreviousResults();
          showNotice(config.text.rate_limited);
        }
        return;
      }
      if (status && status < 500) {
        console.warn('[jaiio-search] requête rejetée (HTTP ' + status + ') — état conservé', e);
        restorePreviousResults();
        return;
      }
      restorePreviousResults();
      if (!options.retriedTransient) {
        waitForRetry(1000, resultsController.signal).then(function () {
          if (token !== currentFetch) return;
          loadFresh({ silent: true, onDone: options.onDone, retriedTransient: true,
            retriedAfter429: options.retriedAfter429 });
        }).catch(function () { /* annulé par de nouveaux critères */ });
      } else {
        console.warn('[jaiio-search] API indisponible après retry — grille conservée', e);
        setEmpty(false);
        setUnavailable(true);
      }
    }

    // Les tailles reprises de la mémoire ne valent que pour les groupes que la page
    // propose : sur « Sacs », taille M renvoie zéro pièce (API, 2026-10-08). Le moteur
    // ne vide la distribution d'un groupe que si la page n'en a pas (celle du groupe
    // filtré reste pleine, TESTS.md § 18.21) : un groupe repris qui revient vide est
    // retiré et la page se recharge sans lui. La mémoire, elle, est gardée pour la
    // prochaine collection qui propose ce groupe. Un seul contrôle, au premier résultat.
    function dropUnofferedSizes(data) {
      if (!injectedSizes) return false;
      var facets = data.facets || {};
      var dropped = injectedSizes.filter(function (key) {
        return !Object.keys(facets[key] || {}).length;
      });
      injectedSizes = null;
      if (!dropped.length) return false;
      dropped.forEach(function (key) { delete state.filters[key]; });
      writeUrl(true);
      emit('filters:change', getFilters());
      return true;
    }

    function applyResultMeta(data) {
      results.total = data.total || (keepSsrGrid() && ssrTotal != null ? ssrTotal : 0);
      results.facets = data.facets || {};
      // Le back renvoie le tri RÉELLEMENT appliqué (ex. -date sur /search sans q,
      // la pertinence n'existant qu'avec q) : on recale le dropdown dessus.
      if (typeof data.sort === 'string') {
        results.appliedSort = data.sort;
        syncSortUi(data.sort);
      }
      // Bornes du slider prix : figées tant qu'un filtre prix est actif
      // (sinon les bornes suivraient la sélection et le slider "sauterait").
      var priceFiltered = state.prixMin != null || state.prixMax != null;
      if (!priceFiltered || !results.priceBounds) {
        results.priceBounds = data.price_range || results.priceBounds;
      }
      renderFacets();
      renderActiveFilters();
      updateCounters();
      setUnavailable(false);
      updateResultHead();
      setEmpty(results.total === 0 && !keepSsrGrid());
      // Distribution/compteurs à jour → la facet-bar externe peut re-render.
      emit('change', getState());
    }

    // Une réponse vide du moteur ne doit JAMAIS détruire la grille rendue serveur. Cas réel :
    // une smart collection Shopify en cours de rebuild (règles modifiées) disparaît
    // temporairement de `product.collections` côté Admin API, donc de l'index — l'API répond
    // alors 200 / total: 0 sur une collection qui a pourtant 20k produits. Mieux vaut les
    // cartes SSR qu'une page vide. La condition sur le DOM garde le garde-fou honnête : après
    // un vidage explicite (fetch non silencieux), l'empty-state reprend la main normalement.
    function keepSsrGrid() {
      return ssrActive && ssrUrl === viewportUrl() &&
        !results.items.length && els.grid.children.length > 0;
    }

    // Shopify sert la même photo sous /cdn/shop/files/*_500x et
    // cdn.shopify.com/s/files/.../files/*?width=500. Comparer l'original,
    // pour garder son vrai img et son cache même si le moteur change d'URL.
    function imageIdentity(value) {
      if (!value) return '';
      try {
        var url = new URL(value, location.origin);
        var path = url.pathname.replace(/^.*\/(?:files|products)\//, '');
        return path.replace(/_\d+x\d*(?=\.[^/.]+$)/, '') + ':' + (url.searchParams.get('v') || '');
      } catch (e) { return value; }
    }

    function refreshRetainedCard(card, item) {
      var previousMarkup = cardMetadataMarkup.get(card) || {};
      var sold = !item.available;
      if (card.classList.contains('product-item--sold-out') !== sold) card.classList.toggle('product-item--sold-out', sold);
      var link = card.querySelector('.product-item__aspect-ratio');
      if (link) {
        setCardAttribute(link, 'href', item.url);
        setCardAttribute(link, 'aria-label', item.title);
      }
      var title = card.querySelector('.product-item-meta__title');
      if (title) {
        if (title.textContent !== item.title) title.textContent = item.title;
        setCardAttribute(title, 'href', item.url);
      }
      var brand = card.querySelector('.product-item-meta__marque');
      if (brand && brand.textContent !== (item.brand || '')) brand.textContent = item.brand || '';
      var image = card.querySelector('.product-item__primary-image');
      var selectedPhoto = card.querySelector('[data-card-media-dot][aria-current="true"]');
      if (image && (!selectedPhoto || selectedPhoto.getAttribute('data-card-media-dot') === '0')) {
        setCardAttribute(image, 'alt', item.image_alt || item.title);
      }
      var mention = card.querySelector('.mention');
      var nextMentionMarkup = renderMention(item);
      if (mention && previousMarkup.mention !== nextMentionMarkup) {
        var template = document.createElement('template');
        template.innerHTML = nextMentionMarkup;
        var nextMention = template.content.firstElementChild;
        if (mention.className !== nextMention.className) mention.className = nextMention.className;
        if (mention.textContent !== nextMention.textContent) mention.textContent = nextMention.textContent;
      }
      var price = card.querySelector('.product-item-meta__price-list-container');
      var nextPrice = renderPrices(item);
      if (price && previousMarkup.price !== nextPrice) {
        var template = document.createElement('template');
        template.innerHTML = nextPrice;
        var nextPriceNode = template.content.firstElementChild;
        // Les espaces insécables du prix sont sérialisés en &nbsp; par le DOM.
        // Comparer les nœuds évite de remplacer un prix identique à chaque reprise.
        if (!price.isEqualNode(nextPriceNode)) price.replaceWith(nextPriceNode);
      }
      cardMetadataMarkup.set(card, { mention: nextMentionMarkup, price: nextPrice });
    }

    function setCardAttribute(element, name, value) {
      value = String(value);
      if (element.getAttribute(name) !== value) element.setAttribute(name, value);
    }

    function prioritizeRetainedCard(card, index) {
      if (index >= 4) return; // même première rangée prioritaire que renderCard
      var image = card.querySelector('.product-item__primary-image');
      if (image) {
        setCardAttribute(image, 'loading', 'eager');
        setCardAttribute(image, 'fetchpriority', 'high');
      }
    }

    function renderGrid(append) {
      els.grid.hidden = false;
      if (!append && keepSsrGrid()) return;
      if (append) els.grid.insertAdjacentHTML('beforeend', results.items.map(renderCard).join(''));
      else {
        var focused = els.grid.contains(document.activeElement) ? document.activeElement : null;
        var productList = els.grid.closest('product-list');
        if (productList) {
          // Le moteur prend la main sur le classement : Focal ne doit plus
          // animer son ancien snapshot SSR, même après un untilVisible tardif.
          setCardAttribute(productList, 'data-jaiio-search-owned', '');
          if (typeof productList.handoffToSearch === 'function') productList.handoffToSearch();
        }
        var existing = new Map();
        Array.from(els.grid.children).forEach(function (card) {
          existing.set(card.dataset.productId, card);
        });
        var wanted = new Set(results.items.map(function (item) { return String(item.id); }));
        // Enlever les absentes avant le classement évite de déplacer toutes
        // les cartes conservées devant un préfixe SSR qui va disparaître.
        existing.forEach(function (card, id) {
          if (!wanted.has(id)) { card.remove(); existing.delete(id); }
        });
        var cards = [];
        results.items.forEach(function (item, index) {
          var id = String(item.id);
          var card = existing.get(id);
          // Une vraie nouvelle photo remplace la carte ; une simple différence
          // de transformation CDN ne relance aucun téléchargement de l'image.
          if (card && card.querySelector('.product-item__info') &&
              cardPrimaryImages.get(card) === imageIdentity(item.image_url)) {
            refreshRetainedCard(card, item);
            prioritizeRetainedCard(card, index);
          } else {
            if (card) card.remove();
            var template = document.createElement('template');
            template.innerHTML = renderCard(item, index);
            card = template.content.firstElementChild;
            cardPrimaryImages.set(card, imageIdentity(item.image_url));
          }
          cards.push(card);
        });
        for (var index = 0; index < cards.length;) {
          var at = els.grid.children[index], card = cards[index];
          if (at === card) { index++; continue; }
          if (card.parentNode === els.grid) {
            els.grid.insertBefore(card, at || null);
            index++;
          } else {
            // Les nouvelles cartes adjacentes arrivent en une mutation, sans
            // déconnecter leurs voisines, images, galeries ou wishlist.
            var fragment = document.createDocumentFragment();
            do { fragment.appendChild(cards[index++]); }
            while (index < cards.length && cards[index].parentNode !== els.grid);
            els.grid.insertBefore(fragment, at || null);
          }
        }
        // insertBefore conserve le nœud mais peut faire perdre son focus natif.
        if (focused && focused.isConnected && document.activeElement !== focused) focused.focus({ preventScroll: true });
      }
      ssrActive = false; // le moteur a pris la main sur la grille
      if (els.ssrPagination) els.ssrPagination.hidden = true;
      root.setAttribute('data-jaiio-hydrated', '');
      refreshCardAttributes(results.items);
      updatePagination();
    }

    function appendGridFrom(startIndex) {
      var html = results.items.slice(startIndex).map(function (item, i) {
        return renderCard(item, startIndex + i);
      }).join('');
      els.grid.insertAdjacentHTML('beforeend', html);
      Array.from(els.grid.children).slice(startIndex).forEach(function (card, index) {
        cardPrimaryImages.set(card, imageIdentity(results.items[startIndex + index].image_url));
      });
      refreshCardAttributes(results.items);
      updatePagination();
    }

    /* ── Chargement progressif (`.cl-pag`, maquette collections) ─────────── */

    function canLoadMore() {
      if (usesShopifyPrice()) return nativeHasMore();
      // Le total Shopify informe le compteur, mais seul un premier lot API
      // réussi autorise l'ajout de cartes. Sinon on dupliquerait le lot SSR.
      return !ssrActive && pageOffset() + results.items.length < results.total && pageOffset() + results.items.length <= MAX_SKIP;
    }

    // Tant que la grille serveur est à l'écran, le moteur n'a rien chargé lui-même :
    // le bloc reste masqué plutôt que d'annoncer « 0 pièce sur N ».
    function updatePagination() {
      if (!els.pag) return;
      var shown = results.items.length;
      var total = results.total;
      els.pag.hidden = ssrActive || !shown || !total;
      if (els.pag.hidden) return;
      var form = config.text.pieces_progress || {};
      var text = els.pag.querySelector('[data-jaiio-pag-text]');
      if (text) text.textContent = pageOffset() ?
        fill(config.text.pieces_page_progress, { first: formatCount(pageOffset() + 1), last: formatCount(pageOffset() + shown), total: formatCount(total) }) :
        fill(shown > 1 ? form.other : form.one, { shown: formatCount(shown), total: formatCount(total) });
      var bar = els.pag.querySelector('[data-jaiio-pag-bar]');
      if (bar) bar.style.width = Math.min(100, Math.round((pageOffset() + shown) / total * 100)) + '%';
      if (els.loadMoreButton) els.loadMoreButton.hidden = !canLoadMore();
    }

    /* ── Scroll infini ─────────────────────────────────────────────────── */

    var loadingMore = false;
    // `focusNew` : au clic sur « Voir plus », le focus passe à la première pièce
    // arrivée. Sinon il resterait sur le bouton, repoussé sous 60 cartes que le
    // clavier devrait remonter une à une pour les atteindre.
    function loadMore(focusNew) {
      if (loadingMore || results.loading) return;
      if (renderedUrl !== viewportUrl() || els.grid.hidden) return;
      if (!canLoadMore()) return;
      loadingMore = true;
      setLoadMoreBusy(true);
      // Jeton anti-course : si un loadFresh démarre pendant la requête (filtre, tri,
      // popstate…), la réponse périmée appendrait 60 produits de l'ancien contexte
      // dans la grille filtrée et écraserait total/take/URL — on la jette.
      var token = currentFetch;
      if (usesShopifyPrice()) {
        var start = results.items.length;
        fetchNativePage(nativeNextPage, resultsController.signal).then(function (chunk) {
          if (token !== currentFetch) return;
          applyNativePage(chunk, true);
          state.take = (nativeNextPage - (state.page || 1)) * SSR_PAGE_SIZE;
          writeUrl(true);
          renderedUrl = viewportUrl();
          requestedUrl = renderedUrl;
          loadingMore = false;
          setLoadMoreBusy(false);
          if (focusNew) focusCard(start);
        }).catch(function () {
          if (token !== currentFetch) return;
          loadingMore = false;
          nativeMoreFailed = true;
          setLoadMoreBusy(false);
          setUnavailable(true);
          root.removeAttribute('data-jaiio-hydrated');
          if (els.ssrPagination) els.ssrPagination.hidden = false;
        });
        return;
      }
      var skip = pageOffset() + results.items.length;
      apiFetch('/search/products', productParams(skip, Math.min(PAGE_SIZE, MAX_RESULT_ITEMS - skip)), { signal: resultsController.signal })
        .then(function (data) {
          if (token !== currentFetch) return; // réponse d'un contexte périmé — ignorée
          results.total = data.total || results.total;
          var start = results.items.length;
          results.items = results.items.concat(data.items || []);
          appendGridFrom(start);
          state.take = results.items.length;
          writeUrl(true); // take dans l'URL pour la restauration au retour
          renderedUrl = viewportUrl();
          requestedUrl = renderedUrl;
          loadingMore = false;
          setLoadMoreBusy(false);
          if (focusNew) focusCard(start);
        })
        .catch(function (e) {
          if (token !== currentFetch) return; // le verrou a déjà été rendu par loadFresh
          loadingMore = false;
          setLoadMoreBusy(false);
          console.error('[jaiio-search] échec load-more', e);
          // Pas de fallback destructif en cours de session : le bouton (ou le
          // défilement suivant) relance la même page.
        });
    }

    function setLoadMoreBusy(busy) {
      if (els.loadMoreButton) els.loadMoreButton.setAttribute('aria-busy', String(busy));
    }

    function focusCard(index) {
      var card = els.grid.children[index];
      var link = card && card.querySelector('a[href]');
      if (link) link.focus();
    }

    if (els.loadMoreButton) {
      els.loadMoreButton.addEventListener('click', function () { loadMore(true); });
    }

    if ('IntersectionObserver' in window && els.sentinel) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          // Défilement continu : le bouton reste un secours clavier/réseau,
          // jamais une étape imposée après les trois premières pages.
          if (entry.isIntersecting) loadMore(false);
        });
      }, { rootMargin: '800px 0px' });
      observer.observe(els.sentinel);
    }

    /* ── Restauration de position, par entrée de l'historique ──────────── */

    function viewportUrl() {
      return window.location.pathname + window.location.search;
    }

    function readViewportState() {
      var vp = history.state && history.state.jaiioViewport;
      // Secours si replaceState était indisponible. Une arrivée neuve sur la
      // collection ne doit jamais reprendre le scroll d'une visite précédente.
      if (!vp) {
        var navigation = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        if (navigation && (navigation.type === 'back_forward' || navigation.type === 'reload')) {
          try { vp = JSON.parse(sessionStorage.getItem(VP_STORAGE_KEY) || 'null'); } catch (e) { /* noop */ }
        }
      }
      if (!vp || vp.url !== viewportUrl() || !Number.isFinite(vp.y) || vp.y < 0) return null;
      return vp;
    }

    function viewportCard(productId) {
      var cards = els.grid.querySelectorAll('[data-product-id]');
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].getAttribute('data-product-id') === productId) return cards[i];
      }
      return null;
    }

    function saveViewportState(productId) {
      // Pendant un remplacement de résultats, ne pas écraser une bonne position
      // par le scroll transitoire d'une grille vide ou d'une ancienne sélection.
      if (!els.grid.children.length || renderedUrl !== viewportUrl()) return;
      var card = productId ? viewportCard(productId) : null;
      if (!card) {
        var cards = els.grid.querySelectorAll('[data-product-id]');
        for (var i = 0; i < cards.length; i++) {
          var rect = cards[i].getBoundingClientRect();
          if (rect.bottom > 0 && rect.top < window.innerHeight) { card = cards[i]; break; }
        }
      }
      var vp = {
        url: viewportUrl(),
        y: window.scrollY,
        take: Math.min(MAX_RESTORE_TAKE, Math.max(PAGE_SIZE, state.take, results.items.length, els.grid.children.length)),
        productId: card ? card.getAttribute('data-product-id') : null,
        offset: card ? card.getBoundingClientRect().top : null
      };
      // Ne remplace pas les données d'autres composants et ne consomme pas le
      // snapshot : plusieurs allers-retours vers des fiches doivent fonctionner.
      try {
        history.replaceState(Object.assign({}, history.state, { jaiioViewport: vp }), '');
      } catch (e) { /* le stockage de session ci-dessous sert de secours */ }
      try { sessionStorage.setItem(VP_STORAGE_KEY, JSON.stringify(vp)); } catch (e) { /* history.state suffit */ }
    }

    els.grid.addEventListener('click', function (event) {
      if (event.defaultPrevented) return;
      var link = event.target.closest('a[href]');
      if (!link) return;
      var url = new URL(link.href, window.location.href);
      // Liens relatifs, absolus, localisés et /collections/…/products/… inclus.
      if (url.origin !== window.location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return;
      var card = link.closest('[data-product-id]');
      saveViewportState(card ? card.getAttribute('data-product-id') : null);
    });

    window.addEventListener('pagehide', function () { saveViewportState(); });

    function restoreViewport() {
      var vp = readViewportState();
      if (!vp || results.loading) return;
      cancelViewportRestore();
      var token = currentFetch;
      var cancelled = false;
      var timers = [];
      var inputs = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
      function cancel() {
        cancelled = true;
        timers.forEach(clearTimeout);
        inputs.forEach(function (type) { window.removeEventListener(type, cancel); });
      }
      cancelViewportRestore = cancel;
      inputs.forEach(function (type) { window.addEventListener(type, cancel, { passive: true }); });
      function restore() {
        if (cancelled || token !== currentFetch || vp.url !== viewportUrl()) return;
        var target = vp.productId ? viewportCard(vp.productId) : null;
        var y = target && Number.isFinite(vp.offset) ?
          window.scrollY + target.getBoundingClientRect().top - vp.offset : vp.y;
        if (Math.abs(y - window.scrollY) > 1) window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
      }
      restore();
      // Le header mobile anime ses hauteurs après le scroll initial. Corriger
      // aussi pendant/après la transition, sans jamais contrer un geste humain.
      requestAnimationFrame(function () { requestAnimationFrame(restore); });
      [50, 100, 150, 200, 250, 300, 700].forEach(function (delay) {
        timers.push(setTimeout(restore, delay));
      });
      timers.push(setTimeout(cancel, 750));
    }

    window.addEventListener('pageshow', function (event) {
      if (event.persisted && renderedUrl === viewportUrl()) restoreViewport();
    });

    /* ── Facettes ──────────────────────────────────────────────────────── */

    function facetValues(facet) {
      var raw = results.facets[facet.key] || {};
      // Tableau + tri EXPLICITE : l'ordre des clés d'un objet JS réordonne les clés
      // integer-like numériquement AVANT les autres — les demi-pointures ("37.5")
      // partiraient en fin de liste après toutes les pointures entières.
      var values = Object.entries(raw).filter(function (entry) {
        return core.isFacetValueVisible(facet.key, entry[0]) || (state.filters[facet.key] || []).indexOf(entry[0]) !== -1;
      }).map(function (entry) {
        return { label: entry[0], count: entry[1] };
      });
      // Les valeurs sélectionnées mais absentes de la distribution restent visibles (count 0).
      (state.filters[facet.key] || []).forEach(function (selected) {
        if (!values.some(function (v) { return v.label === selected; })) {
          values.push({ label: selected, count: 0 });
        }
      });
      if (facet.key === 'pointure') {
        // Numérique croissant (35, 36, 37.5, 38…) ; non-numériques en fin, ordre conservé.
        values.sort(function (a, b) {
          var na = toNumber(a.label), nb = toNumber(b.label);
          if (na == null && nb == null) return 0;
          if (na == null) return 1;
          if (nb == null) return -1;
          return na - nb;
        });
      } else if (facet.key === 'taille') {
        // Ordre canonique XXS → 4XL, puis les valeurs inconnues (ordre d'origine conservé).
        values.sort(function (a, b) {
          var ia = SIZE_ORDER.indexOf(String(a.label).toUpperCase());
          var ib = SIZE_ORDER.indexOf(String(b.label).toUpperCase());
          if (ia === -1 && ib === -1) return 0;
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
      }
      // Autres facettes : ordre de l'API conservé (count desc côté Meilisearch).
      return values;
    }

    function facetSelectedSummary(facet) {
      if (facet.type === 'price') {
        if (state.prixMin == null && state.prixMax == null) return '';
        var bounds = results.priceBounds || { min: 0, max: 0 };
        var min = state.prixMin != null ? state.prixMin : bounds.min;
        var max = state.prixMax != null ? state.prixMax : bounds.max;
        return formatMoney(min) + ' – ' + formatMoney(max);
      }
      var selected = state.filters[facet.key] || [];
      if (!selected.length) return '';
      if (facet.valueLabels) {
        return selected.map(function (v) { return facet.valueLabels[v] || v; }).join(', ');
      }
      return selected.join(', ');
    }

    function renderFacet(facet) {
      var isOpen = !!openFacets[facet.key];
      var selectedSummary = facetSelectedSummary(facet);
      var bodyHtml = '';

      if (facet.type === 'price') {
        bodyHtml = renderPriceFacetBody();
      } else {
        var values = facetValues(facet);
        if (!values.length) return ''; // facette vide sur ce contexte → masquée
        var term = facetSearchTerms[facet.key] || '';
        var searchBox = '';
        if (facet.searchable) {
          searchBox = '<div class="jaiio-facet__search">' +
            '<input type="search" class="jaiio-facet__search-input" placeholder="' + esc(config.text.facet_search_placeholder) + '"' +
            ' value="' + esc(term) + '" data-jaiio-facet-search="' + facet.key + '" autocomplete="off">' +
            '</div>';
        }
        var filtered = term
          ? values.filter(function (v) { return normalize(v.label).indexOf(normalize(term)) !== -1; })
          : values;

        var itemsHtml = filtered.map(function (value, idx) {
          var id = 'jaiio-f-' + facet.key + '-' + idx;
          var checked = (state.filters[facet.key] || []).indexOf(value.label) !== -1;
          var displayLabel = facet.valueLabels ? (facet.valueLabels[value.label] || value.label) : value.label;

          if (facet.type === 'swatch') {
            return '<div class="color-swatch' + (normalize(value.label) === 'blanc' ? ' color-swatch--white' : '') +
              (value.count === 0 && !checked ? ' is-disabled' : '') + '" data-tooltip="' + esc(value.label) + '">' +
              '<input class="color-swatch__radio visually-hidden" type="checkbox" id="' + id + '"' +
              ' value="' + esc(value.label) + '" data-jaiio-filter="' + facet.key + '"' + (checked ? ' checked' : '') + '>' +
              '<label class="color-swatch__item" for="' + id + '" style="' + swatchStyle(value.label) + '" title="' + esc(value.label) + ' (' + value.count + ')">' +
              '<span class="visually-hidden">' + esc(value.label) + '</span>' +
              '</label></div>';
          }
          if (facet.type === 'box') {
            return '<div class="block-swatch' + (value.count === 0 && !checked ? ' is-disabled' : '') + '">' +
              '<input class="block-swatch__radio visually-hidden" type="checkbox" id="' + id + '"' +
              ' value="' + esc(value.label) + '" data-jaiio-filter="' + facet.key + '"' + (checked ? ' checked' : '') + '>' +
              '<label class="block-swatch__item" for="' + id + '" title="' + esc(value.label) + ' (' + value.count + ')">' + esc(displayLabel) + '</label>' +
              '</div>';
          }
          // list / single
          return '<div class="checkbox-container">' +
            '<input class="checkbox" type="checkbox" id="' + id + '" value="' + esc(value.label) + '"' +
            ' data-jaiio-filter="' + facet.key + '"' + (facet.type === 'single' ? ' data-jaiio-single' : '') +
            (checked ? ' checked' : '') + '>' +
            '<label for="' + id + '">' + esc(displayLabel) + ' <span class="jaiio-facet__count">(' + value.count + ')</span></label>' +
            '</div>';
        }).join('');

        var listClass = facet.type === 'swatch' ? 'color-swatch-list'
          : facet.type === 'box' ? 'block-swatch-list block-swatch-list--small'
          : 'jaiio-facet__checkbox-list';
        bodyHtml = searchBox + '<div class="jaiio-facet__values ' + listClass + '">' + itemsHtml + '</div>';
      }

      if (!bodyHtml) return '';

      return '<div class="product-facet__filter-item jaiio-facet" data-jaiio-facet-item="' + facet.key + '">' +
        '<button type="button" class="collapsible-toggle text--strong" aria-expanded="' + (isOpen ? 'true' : 'false') + '"' +
        ' data-jaiio-facet-toggle="' + facet.key + '">' +
        esc(facet.label) +
        (selectedSummary ? '<span class="collapsible-toggle__selected-value text--subdued text--small">' + esc(selectedSummary) + '</span>' : '') +
        config.icons.chevron +
        '</button>' +
        '<div class="jaiio-facet__body"' + (isOpen ? '' : ' hidden') + '>' + bodyHtml + '</div>' +
        '</div>';
    }

    function renderPriceFacetBody() {
      var bounds = results.priceBounds;
      if (!bounds || bounds.min == null || bounds.max == null || bounds.min >= bounds.max) return '';
      var lo = Math.floor(bounds.min);
      var hi = Math.ceil(bounds.max);
      var curMin = state.prixMin != null ? state.prixMin : lo;
      var curMax = state.prixMax != null ? state.prixMax : hi;
      var pMin = ((curMin - lo) / (hi - lo)) * 100;
      var pMax = ((curMax - lo) / (hi - lo)) * 100;

      return '<div class="price-range jaiio-price-range" data-jaiio-price data-min-bound="' + lo + '" data-max-bound="' + hi + '">' +
        '<div class="price-range__range-group range-group" style="--range-min: ' + pMin + '%; --range-max: ' + pMax + '%">' +
        '<input type="range" class="range" aria-label="' + esc(config.text.price_from) + '" min="' + lo + '" max="' + hi + '" step="1" value="' + Math.floor(curMin) + '" data-jaiio-price-range="min">' +
        '<input type="range" class="range" aria-label="' + esc(config.text.price_to) + '" min="' + lo + '" max="' + hi + '" step="1" value="' + Math.ceil(curMax) + '" data-jaiio-price-range="max">' +
        '</div>' +
        '<div class="price-range__input-group">' +
        '<div class="price-range__input input-prefix text--xsmall">' +
        '<span class="input-prefix__value text--subdued">' + esc(config.currency_symbol) + '</span>' +
        '<input class="input-prefix__field" type="number" inputmode="numeric" aria-label="' + esc(config.text.price_from) + '"' +
        ' min="' + lo + '" max="' + hi + '" placeholder="' + lo + '"' +
        (state.prixMin != null ? ' value="' + Math.floor(curMin) + '"' : '') + ' data-jaiio-price-input="min">' +
        '</div>' +
        '<span class="price-range__delimiter text--small">' + esc(config.text.price_range_to) + '</span>' +
        '<div class="price-range__input input-prefix text--xsmall">' +
        '<span class="input-prefix__value text--subdued">' + esc(config.currency_symbol) + '</span>' +
        '<input class="input-prefix__field" type="number" inputmode="numeric" aria-label="' + esc(config.text.price_to) + '"' +
        ' min="' + lo + '" max="' + hi + '" placeholder="' + hi + '"' +
        (state.prixMax != null ? ' value="' + Math.ceil(curMax) + '"' : '') + ' data-jaiio-price-input="max">' +
        '</div>' +
        '</div>' +
        '</div>';
    }

    function renderFacets() {
      if (!els.facets) return;
      els.facets.innerHTML = FACETS.map(renderFacet).join('');
    }

    /* ── Filtres actifs (breadcrumb) ───────────────────────────────────── */

    function activeFilterEntries() {
      var entries = [];
      FACETS.forEach(function (facet) {
        if (facet.type === 'price') {
          if (state.prixMin != null || state.prixMax != null) {
            var bounds = results.priceBounds || { min: 0, max: 0 };
            var min = state.prixMin != null ? state.prixMin : bounds.min;
            var max = state.prixMax != null ? state.prixMax : bounds.max;
            entries.push({ key: 'prix', value: null, label: formatMoney(min) + ' – ' + formatMoney(max) });
          }
          return;
        }
        (state.filters[facet.key] || []).forEach(function (value) {
          var label = facet.valueLabels ? (facet.valueLabels[value] || value) : value;
          entries.push({ key: facet.key, value: value, label: label });
        });
      });
      return entries;
    }

    function renderActiveFilters() {
      if (!els.activeFilters) return;
      var entries = activeFilterEntries();
      if (!entries.length) {
        els.activeFilters.hidden = true;
        els.activeFilters.innerHTML = '';
        return;
      }
      var html = entries.map(function (entry) {
        return '<div class="tag">' +
          '<button type="button" class="tag__icon tap-area" data-jaiio-remove-filter="' + esc(entry.key) + '"' +
          (entry.value != null ? ' data-jaiio-remove-value="' + esc(entry.value) + '"' : '') +
          ' aria-label="' + esc(config.text.remove) + '">' + config.icons.close + '</button>' +
          esc(entry.label) +
          '</div>';
      }).join('');
      html += '<button type="button" class="tag-link link text--subdued" data-jaiio-clear-all>' + esc(config.text.clear_all) + '</button>';
      els.activeFilters.innerHTML = html;
      els.activeFilters.hidden = false;
    }

    function updateCounters() {
      var count = activeFilterEntries().length;
      els.activeCounts.forEach(function (el) {
        el.textContent = String(count);
        el.hidden = count === 0;
      });
      if (els.viewItems) {
        els.viewItems.textContent = config.text.view_items + ' (' + results.total + ')';
      }
    }

    /* ── En-tête de résultats (`.rs-head`, maquette recherche) ─────────────
       Le serveur ne connaît pas le total : il rend le titre sans nombre et le
       moteur le réécrit ici, à chaque réponse. */

    function updateResultHead() {
      if (!els.rsTitle) return;
      var T_RS = config.text.results || {};
      if (results.total === 0 && els.rsTop) {
        // Zéro résultat : « Aucune pièce pour « q » aujourd'hui » — le même h1,
        // pour qu'il n'y en ait jamais deux (T055).
        var T_Z = zeroText();
        els.rsTitle.innerHTML = state.q ? fill(T_Z.title, { terms: esc(state.q) }) : (T_Z.title_filters || '');
      } else if (!state.q) {
        els.rsTitle.textContent = T_RS.title_all || '';
      } else {
        var form = results.total === 1 ? T_RS.title_count_one : T_RS.title_count_other;
        els.rsTitle.innerHTML = fill(form, { count: formatCount(results.total), terms: esc(state.q) });
      }
      updateResultContext(T_RS);
    }

    // « Triées par pertinence · 3 marques · tailles 34 à 42 » : ce que la page
    // montre déjà, dit en une ligne. Chaque morceau tombe s'il n'apprend rien.
    function updateResultContext(T_RS) {
      if (!els.rsContext) return;
      var parts = [];
      if (T_RS.sorted_by) {
        parts.push(fill(T_RS.sorted_by, { sort: sortLabelFor(results.appliedSort || state.sort) }));
      }
      var brands = Object.keys(results.facets.marque || {}).length;
      if (brands > 0 && T_RS.brands_count_one) {
        parts.push(fill(brands === 1 ? T_RS.brands_count_one : T_RS.brands_count_other, { count: formatCount(brands) }));
      }
      var sizes = Object.keys(results.facets.taille || {});
      // Les tailles se disent en étendue quand elles sont chiffrées (34 à 42) et en
      // nombre sinon : « XS à XXL » supposerait un ordre que la distribution n'a pas.
      var numeric = sizes.map(toNumber).filter(function (n) { return n !== null; });
      if (numeric.length > 1 && T_RS.sizes_range) {
        parts.push(fill(T_RS.sizes_range, {
          from: formatCount(Math.min.apply(null, numeric)),
          to: formatCount(Math.max.apply(null, numeric))
        }));
      } else if (sizes.length > 0 && T_RS.sizes_count_one) {
        parts.push(fill(sizes.length === 1 ? T_RS.sizes_count_one : T_RS.sizes_count_other, { count: formatCount(sizes.length) }));
      }
      els.rsContext.textContent = parts.join(' · ');
      els.rsContext.hidden = parts.length === 0 || results.total === 0;
    }

    // Raccourci marque : la marque doit être NOMMÉE dans la requête (tous ses mots
    // présents en entier), pas seulement dominante dans les facettes — « robe sézane »
    // renvoie plus de Maje que de Sézane, la recherche étant en OU.
    //
    // Le lien vient de `brand_url`, calculé par le back à la réindexation depuis les
    // seules collections marque « pures » : une collection protégée ne peut pas en
    // sortir. La garde sur le préfixe reste, au cas où le contrat bougerait.
    function updateBrandShortcut(T_RS) {
      if (!els.rsBrand) return;
      var found = state.q ? matchBrandInQuery(state.q) : null;
      if (!found) {
        els.rsBrand.hidden = true;
        return;
      }
      var initials = els.rsBrand.querySelector('[data-rs-brand-initials]');
      var name = els.rsBrand.querySelector('[data-rs-brand-name]');
      var desc = els.rsBrand.querySelector('[data-rs-brand-desc]');
      var link = els.rsBrand.querySelector('[data-rs-brand-link]');
      if (initials) initials.textContent = found.label.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 2).toUpperCase();
      if (name) name.textContent = found.label;
      if (desc) {
        desc.textContent = fill(found.count === 1 ? T_RS.brand_desc_one : T_RS.brand_desc_other, { count: formatCount(found.count) });
      }
      if (link) link.setAttribute('href', found.url);
      els.rsBrand.hidden = false;
    }

    function words(value) {
      return normalize(value).split(/[^a-z0-9]+/).filter(Boolean);
    }

    function matchBrandInQuery(query) {
      var asked = words(query);
      if (!asked.length) return null;
      var dist = results.facets.marque || {};
      var best = null;
      Object.keys(dist).forEach(function (label) {
        var parts = words(label);
        if (!parts.length) return;
        var named = parts.every(function (part) { return asked.indexOf(part) !== -1; });
        // La marque la plus précise l'emporte : « Saint Laurent » avant « Saint ».
        if (named && (!best || parts.length > best.parts)) {
          best = { label: label, parts: parts.length, count: dist[label] };
        }
      });
      if (!best) return null;
      var item = results.items.filter(function (entry) {
        return normalize(entry.brand || '') === normalize(best.label) &&
          typeof entry.brand_url === 'string' &&
          entry.brand_url.indexOf('/collections/marque-') === 0;
      })[0];
      if (!item) return null;
      return { label: best.label, count: best.count, url: item.brand_url };
    }

    /* ── Zéro résultat (`.zero`, recherche.dc.html, T055) ──────────────────
       Jamais d'impasse : alerte en premier, reformulations chiffrées, quatre
       nouveautés. Rien de tout cela ne coûte un appel quand il y a des résultats :
       tout part de `setEmpty(true)`, que seul un total nul déclenche. */

    var ZERO_TRIES = 4;  // reformulations comptées, en parallèle
    var ZERO_CARDS = 4;  // nouveautés sous le bloc
    // Une catégorie n'est « celle de la reformulation » que si elle en porte au
    // moins la moitié : « noir » couvre robes, sacs et bottes, et proposer les
    // nouveautés de la plus grosse des trois serait un choix au hasard.
    var ZERO_CATEGORY_SHARE = 0.5;

    var zeroI18n = null;
    function zeroText() {
      if (zeroI18n) return zeroI18n;
      var node = document.querySelector('[data-rs-zero-i18n]');
      try { zeroI18n = JSON.parse(node ? node.textContent : '{}'); } catch (e) { zeroI18n = {}; }
      return zeroI18n;
    }

    // État vide : `/search` a son zéro résultat, les collections leur `.cempty`.
    function setEmpty(show) {
      if (els.rsTop) toggleZero(show);
      else if (els.empty) els.empty.hidden = !show;
    }

    function setUnavailable(show) {
      if (els.unavailable) els.unavailable.hidden = !show;
    }

    function toggleZero(show) {
      els.rsTop.classList.toggle('is-zero', show);
      if (els.rsPage) {
        els.rsPage.classList.toggle('rs-page--zero', show);
        // Sans filtre actif, la barre de facettes n'a rien à proposer (distribution
        // vide) et la maquette ne la montre pas ; avec des filtres, elle reste : c'est
        // par elle qu'on les retire.
        els.rsPage.classList.toggle('rs-page--zero-bare', show && !activeFilterEntries().length);
      }
      els.zeroOnly.forEach(function (el) { el.hidden = !show; });
      // Remis à zéro dans les deux sens : un zéro résultat en remplace un autre
      // (nouvelle requête, filtre retiré) sans garder les pistes du précédent.
      if (els.zeroTry) els.zeroTry.hidden = true;
      if (els.zeroList) els.zeroList.innerHTML = '';
      if (els.zeroAll) els.zeroAll.hidden = true;
      if (els.zeroSec) els.zeroSec.hidden = true;
      if (show) showZero();
    }

    // Un nombre ou une taille seuls (« 46 ») ramènent des pièces de toutes sortes :
    // ce n'est pas une piste, et chaque appel compte.
    function usefulWord(word) {
      return word.length >= 3 && !/^\d+$/.test(word);
    }

    // Une reformulation : le lien qu'on propose et l'appel qui la chiffre, bâtis
    // sur la même requête pour que le nombre affiché soit celui qu'on trouvera.
    // `keepFilters` : les filtres de la visiteuse suivent la reformulation.
    function zeroTry(q, keepFilters, unfiltered) {
      var link = keepFilters ? stateParams() : new URLSearchParams();
      var api = keepFilters ? productParams(0, 1) : new URLSearchParams({ skip: '0', take: '1' });
      [link, api].forEach(function (params) {
        if (q) params.set('q', q); else params.delete('q');
        params.delete('sort'); // pertinence, le défaut avec une requête
      });
      link.delete('take');
      link.delete('page');
      link.delete('sort_by');
      // `take=1` et non 0 : le back refuse `take=0` (400, `@Min(1)`), relevé le
      // 2026-09-28 contre l'hypothèse de Q32. Une pièce de trop, pas davantage.
      var qs = link.toString();
      return {
        q: q,
        unfiltered: unfiltered,
        href: config.routes.search_url + (qs ? '?' + qs : ''),
        params: api
      };
    }

    // Reformulations par retrait de mots (Q82, voie b) : on ne sait pas quel mot
    // est une marque ou une taille, on se contente d'en retirer.
    //
    // Le moteur (Meilisearch, `matchingStrategy` « last » par défaut) retire déjà
    // lui-même les mots de FIN tant qu'il ne trouve rien : un total nul veut dire
    // que le PREMIER mot ne donne rien, seul ou accompagné (« zzz burberry » : 0,
    // « burberry zzz » : 114). Toute reformulation qui le garde vaudrait zéro : on
    // retire donc par le début, puis on essaie les mots seuls.
    //
    // Des filtres actifs peuvent aussi être la cause : la première piste est alors
    // la même requête sans eux, et les autres gardent les filtres.
    function zeroCandidates() {
      var out = [];
      var seen = {};
      var filtered = activeFilterEntries().length > 0;
      function add(q, keepFilters) {
        var key = normalize(q || '') + '|' + keepFilters;
        if (seen[key]) return;
        seen[key] = true;
        out.push(zeroTry(q, keepFilters, filtered && !keepFilters));
      }
      if (filtered) add(state.q, false);
      var w = String(state.q || '').trim().split(/\s+/).filter(Boolean);
      for (var k = 1; k < w.length; k++) {
        var rest = w.slice(k);
        if (rest.length > 1 || usefulWord(rest[0])) add(rest.join(' '), filtered);
      }
      for (var i = 1; i < w.length; i++) {
        if (usefulWord(w[i])) add(w[i], filtered);
      }
      return out.slice(0, ZERO_TRIES);
    }

    function showZero() {
      var token = currentFetch;
      var T_Z = zeroText();
      var tries = zeroCandidates();
      // Chaque reformulation vit seule : en échec (429, délai) ou à zéro, sa ligne
      // disparaît et le reste du bloc tient.
      Promise.all(tries.map(function (entry) {
        return apiFetch('/search/products', entry.params).then(function (data) {
          return { entry: entry, total: data.total || 0, facets: data.facets || {} };
        }).catch(function (e) {
          console.warn('[jaiio-search] reformulation non chiffrée : « ' + (entry.q || '') + ' »', e);
          return null;
        });
      })).then(function (rows) {
        if (token !== currentFetch) return null;
        rows = rows.filter(function (row) { return row && row.total > 0; });
        renderZeroTries(rows, T_Z);
        // Pour l'assistant (T057) : il reprendra ces pistes en chips sans les recompter.
        emit('zero', {
          q: state.q,
          tries: rows.map(function (row) { return { q: row.entry.q, total: row.total, url: row.entry.href }; })
        });
        return loadZeroNew(zeroCategory(rows), token, T_Z);
      });
    }

    function renderZeroTries(rows, T_Z) {
      if (!els.zeroList || !els.zeroTry) return;
      // Les chaînes de locale sont déjà échappées par `t` : seule la requête l'est ici.
      els.zeroList.innerHTML = rows.map(function (row) {
        var what = '<b>' + (row.entry.q ? esc(row.entry.q) : T_Z.all_pieces) + '</b>';
        if (row.entry.unfiltered) what += ' · ' + T_Z.without_filters;
        var count = fill(row.total === 1 ? T_Z.count_one : T_Z.count_other, { count: formatCount(row.total) });
        return '<li><a href="' + esc(row.entry.href) + '"><span>' + what + '</span>' +
          '<span class="rs-zero__n">' + count + '</span></a></li>';
      }).join('');
      els.zeroTry.hidden = rows.length === 0;
    }

    // Catégorie « saillante » : la sous-catégorie qui domine la première
    // reformulation qui en a une (« manteau noir » → Manteau). `sous_categorie`
    // et non `categorie`, qui ne porte que Vêtements / Chaussures / Sacs.
    function zeroCategory(rows) {
      for (var i = 0; i < rows.length; i++) {
        var dist = rows[i].facets.sous_categorie || {};
        var best = null;
        Object.keys(dist).forEach(function (label) {
          if (!best || dist[label] > dist[best]) best = label;
        });
        if (best && dist[best] >= rows[i].total * ZERO_CATEGORY_SHARE) return best;
      }
      return null;
    }

    function fetchZeroNew(category) {
      var params = new URLSearchParams();
      if (category) params.append('sous_categorie', category);
      params.set('sort', '-date');
      params.set('skip', '0');
      params.set('take', String(ZERO_CARDS));
      return apiFetch('/search/products', params).then(function (data) { return data.items || []; });
    }

    // Nouveautés de la catégorie, sinon du catalogue : les quatre cartes sont
    // toujours là, sauf si le moteur ne répond plus du tout — la section se tait
    // alors, le bloc au-dessus reste entier.
    function loadZeroNew(category, token, T_Z) {
      var first = category
        ? fetchZeroNew(category).catch(function () { return []; })
        : Promise.resolve([]);
      return first.then(function (items) {
        if (items.length >= ZERO_CARDS) return { items: items, category: category };
        return fetchZeroNew(null).then(function (all) { return { items: all, category: null }; });
      }).then(function (found) {
        if (token !== currentFetch) return;
        setZeroLinks(found.category, T_Z);
        renderZeroNew(found, T_Z);
      }, function (e) {
        if (token !== currentFetch) return;
        console.warn('[jaiio-search] nouveautés du zéro résultat indisponibles', e);
        setZeroLinks(null, T_Z);
      });
    }

    // « Voir la catégorie Manteau » ou « Voir les nouveautés » : la page de
    // recherche filtrée, sans requête (tri par date, le défaut du back sans `q`).
    // Pas de lien vers une collection : le thème ne sait pas laquelle correspond.
    function zeroCategoryUrl(category) {
      if (!category) return config.routes.search_url;
      return config.routes.search_url + '?' + new URLSearchParams({ sous_categorie: category }).toString();
    }

    function setZeroLinks(category, T_Z) {
      if (!els.zeroAll) return;
      els.zeroAll.setAttribute('href', zeroCategoryUrl(category));
      els.zeroAll.innerHTML = category ? fill(T_Z.all_category, { category: esc(category) }) : T_Z.all_new;
      els.zeroAll.hidden = false;
    }

    function renderZeroNew(found, T_Z) {
      if (!els.zeroSec || !els.zeroGrid || !found.items.length) return;
      els.zeroSecTitle.innerHTML = found.category
        ? fill(T_Z.sec_title_category, { category: esc(found.category) })
        : T_Z.sec_title;
      els.zeroSecLink.setAttribute('href', zeroCategoryUrl(found.category));
      els.zeroSecLink.innerHTML = found.category ? T_Z.sec_link_category : T_Z.sec_link;
      // Index décalé : ces cartes sont sous le bloc, leurs images n'ont pas à
      // passer avant le reste de la page.
      els.zeroGrid.innerHTML = found.items.slice(0, ZERO_CARDS).map(function (item, i) {
        return renderCard(item, ZERO_CARDS + i);
      }).join('');
      els.zeroSec.hidden = false;
    }

    /* ── Tri ───────────────────────────────────────────────────────────── */

    function sortLabelFor(value) {
      if (value === 'r') return SORT_RELEVANCE_LABEL;
      if (config.page_type === 'collection' && value === '-date') return T_SORT.published_newest;
      if (config.page_type === 'collection' && value === 'date') return T_SORT.published_oldest;
      var found = SORTS.filter(function (s) { return s.value === value; })[0];
      return found ? found.label : SORTS[0].label;
    }

    // Recale l'UI de tri (label + radio cochée) sur le tri réellement appliqué par le
    // back, SANS toucher state.sort ni l'URL (pas de sort= parasite dans la query).
    function syncSortUi(applied) {
      if (els.sortLabel) els.sortLabel.textContent = sortLabelFor(applied);
      if (!els.sortChoices) return;
      els.sortChoices.querySelectorAll('input[name="jaiio_sort"]').forEach(function (radio) {
        radio.checked = radio.value === applied;
      });
    }

    function renderSortChoices() {
      if (!els.sortChoices) return;
      var options = SORTS.slice();
      if (config.page_type === 'collection' && (state.sort === '-date' || state.sort === 'date')) {
        options.unshift({ value: state.sort, label: sortLabelFor(state.sort) });
      }
      if (config.default_sort === 'r') options.unshift({ value: 'r', label: SORT_RELEVANCE_LABEL });
      els.sortChoices.innerHTML = options.map(function (option) {
        return '<label class="popover__choice-item">' +
          '<input type="radio" class="visually-hidden" name="jaiio_sort" value="' + esc(option.value) + '"' +
          (state.sort === option.value ? ' checked' : '') + '>' +
          '<span class="popover__choice-label">' + esc(option.label) + '</span>' +
          '</label>';
      }).join('');
      if (els.sortLabel) els.sortLabel.textContent = sortLabelFor(state.sort);
    }

    var sortPopoverOpen = false;
    var facetsPanelOpen = false;
    var facetsReturnFocus = null;

    // Verrou scroll dédié : .lock-mobile du thème ne couvre que ≤ 740px alors que le
    // panneau et le popover tri vivent jusqu'à 999px — .jaiio-lock (html) couvre ≤ 999px.
    function updateScrollLock() {
      document.documentElement.classList.toggle('jaiio-lock', facetsPanelOpen || sortPopoverOpen);
    }

    function openSortPopover(open) {
      if (!els.sortPopover) return;
      els.sortPopover.toggleAttribute('open', open);
      if (els.sortButton) els.sortButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      sortPopoverOpen = open;
      updateScrollLock();
    }

    /* ── Panneau facettes mobile (role="dialog" côté sections) ────────────── */

    function openFacetsPanel(open) {
      var wasOpen = facetsPanelOpen;
      facetsPanelOpen = open;
      root.classList.toggle('jaiio-facets-open', open);
      updateScrollLock();
      // A11y : focus déplacé dans le dialog à l'ouverture, rendu au déclencheur à la
      // fermeture (Échap déjà géré par le keydown global).
      if (open && !wasOpen) {
        facetsReturnFocus = document.activeElement;
        var closeButton = els.facetsPanel && els.facetsPanel.querySelector('[data-jaiio-close-facets]');
        if (closeButton && closeButton.focus) closeButton.focus();
      } else if (!open && wasOpen) {
        if (facetsReturnFocus && facetsReturnFocus.focus && document.contains(facetsReturnFocus)) {
          facetsReturnFocus.focus();
        }
        facetsReturnFocus = null;
      }
    }

    /* ── Application d'un changement d'état ────────────────────────────── */

    function applyChange(descriptor) {
      state.take = PAGE_SIZE;
      state.page = 1;
      results.priceBounds = (state.prixMin != null || state.prixMax != null) ? results.priceBounds : null;
      writeUrl(false);
      rememberSizes();
      dataLayerPush({
        event: 'jaiio_filter',
        filter_action: descriptor && descriptor.action,
        filter_key: descriptor && descriptor.key,
        filter_value: descriptor && descriptor.value,
        page_type: config.page_type,
        collection: config.collection
      });
      emit('filters:change', getFilters());
      scrollToResults();
      loadFresh();
    }

    function toggleFilterValue(key, value, single) {
      var current = state.filters[key] || [];
      var index = current.indexOf(value);
      var action;
      if (single) {
        if (index !== -1) { state.filters[key] = []; action = 'remove'; }
        else { state.filters[key] = [value]; action = 'add'; }
      } else if (index !== -1) {
        current.splice(index, 1);
        state.filters[key] = current;
        action = 'remove';
      } else {
        current.push(value);
        state.filters[key] = current;
        action = 'add';
      }
      applyChange({ action: action, key: key, value: value });
    }

    function clearAllFilters() {
      state.filters = {};
      state.prixMin = null;
      state.prixMax = null;
      applyChange({ action: 'clear_all' });
    }

    /* ── API publique : actions programmatiques (facet-bar externe) ──────────
     * Rebranchent l'écriture de la facet-bar du thème sur le moteur, en
     * réutilisant le chemin de commit interne (applyChange = writeUrl + emit +
     * loadFresh). Cf. RACCORD-facet-bar.md.
     * ──────────────────────────────────────────────────────────────────────── */

    function apiSetFilter(key, values) {
      if (FILTER_KEYS.indexOf(key) === -1) return window.jaiioSearch;
      state.filters[key] = (values || []).slice();
      applyChange({ action: 'set', key: key, value: (values || []).join(',') });
      return window.jaiioSearch;
    }

    // Mise à jour atomique de plusieurs facettes (notamment Taille + Pointure).
    function apiSetFilters(patch) {
      var changed = [];
      Object.keys(patch || {}).forEach(function (key) {
        if (FILTER_KEYS.indexOf(key) === -1) return;
        state.filters[key] = (patch[key] || []).map(String);
        changed.push(key);
      });
      if (changed.length) applyChange({ action: 'set_many', key: changed.join(','), value: '' });
      return window.jaiioSearch;
    }

    function apiToggleFilter(key, value) {
      if (FILTER_KEYS.indexOf(key) === -1) return window.jaiioSearch;
      toggleFilterValue(key, value, false);
      return window.jaiioSearch;
    }

    function apiSetPrice(min, max) {
      state.prixMin = (min == null || min === '') ? null : toNumber(min);
      state.prixMax = (max == null || max === '') ? null : toNumber(max);
      applyChange({ action: 'price', key: 'prix',
        value: (state.prixMin == null ? '' : state.prixMin) + '-' + (state.prixMax == null ? '' : state.prixMax) });
      return window.jaiioSearch;
    }

    function apiSetSort(value) {
      if (SUPPORTED_SORTS.indexOf(value) === -1) {
        return window.jaiioSearch;
      }
      state.sort = value;
      if (els.sortLabel) els.sortLabel.textContent = sortLabelFor(state.sort);
      state.take = PAGE_SIZE;
      state.page = 1;
      writeUrl(false);
      scrollToResults();
      loadFresh();
      return window.jaiioSearch;
    }

    function apiClear(key) {
      if (key == null) { clearAllFilters(); return window.jaiioSearch; }
      if (key === 'prix') {
        state.prixMin = null;
        state.prixMax = null;
        applyChange({ action: 'remove', key: 'prix' });
      } else if (FILTER_KEYS.indexOf(key) !== -1) {
        delete state.filters[key];
        applyChange({ action: 'clear', key: key });
      }
      return window.jaiioSearch;
    }

    window.jaiioSearch.setFilter = apiSetFilter;
    window.jaiioSearch.setFilters = apiSetFilters;
    window.jaiioSearch.toggleFilter = apiToggleFilter;
    window.jaiioSearch.setPrice = apiSetPrice;
    window.jaiioSearch.setSort = apiSetSort;
    window.jaiioSearch.clear = apiClear;

    /* ── Événements délégués ───────────────────────────────────────────── */

    root.addEventListener('change', function (event) {
      var input = event.target;
      if (input.hasAttribute('data-jaiio-filter')) {
        toggleFilterValue(input.getAttribute('data-jaiio-filter'), input.value, input.hasAttribute('data-jaiio-single'));
        return;
      }
      if (input.name === 'jaiio_sort') {
        state.sort = input.value;
        openSortPopover(false);
        if (els.sortLabel) els.sortLabel.textContent = sortLabelFor(state.sort);
        state.take = PAGE_SIZE;
        state.page = 1;
        writeUrl(false);
        scrollToResults();
        loadFresh();
        return;
      }
      if (input.hasAttribute('data-jaiio-price-range') || input.hasAttribute('data-jaiio-price-input')) {
        handlePriceChange(input);
      }
    });

    // Feedback visuel du double slider pendant le drag (sans requête).
    root.addEventListener('input', function (event) {
      var input = event.target;
      if (input.hasAttribute('data-jaiio-facet-search')) {
        facetSearchTerms[input.getAttribute('data-jaiio-facet-search')] = input.value;
        refreshSearchableFacet(input);
        return;
      }
      if (!input.hasAttribute('data-jaiio-price-range')) return;
      var container = input.closest('[data-jaiio-price]');
      if (!container) return;
      var lo = parseFloat(container.getAttribute('data-min-bound'));
      var hi = parseFloat(container.getAttribute('data-max-bound'));
      var ranges = container.querySelectorAll('[data-jaiio-price-range]');
      var vMin = parseFloat(ranges[0].value), vMax = parseFloat(ranges[1].value);
      if (vMin > vMax) { var tmp = vMin; vMin = vMax; vMax = tmp; }
      var group = container.querySelector('.range-group');
      group.style.setProperty('--range-min', (((vMin - lo) / (hi - lo)) * 100) + '%');
      group.style.setProperty('--range-max', (((vMax - lo) / (hi - lo)) * 100) + '%');
    });

    function refreshSearchableFacet(input) {
      // Re-rend uniquement la liste de valeurs de la facette concernée en gardant le focus.
      var key = input.getAttribute('data-jaiio-facet-search');
      var facet = FACETS.filter(function (f) { return f.key === key; })[0];
      var item = root.querySelector('[data-jaiio-facet-item="' + key + '"]');
      if (!facet || !item) return;
      var listEl = item.querySelector('.jaiio-facet__values');
      if (!listEl) return;
      var temp = document.createElement('div');
      temp.innerHTML = renderFacet(facet);
      var freshList = temp.querySelector('.jaiio-facet__values');
      if (freshList) listEl.innerHTML = freshList.innerHTML;
    }

    function handlePriceChange(input) {
      var container = input.closest('[data-jaiio-price]');
      if (!container) return;
      var lo = parseFloat(container.getAttribute('data-min-bound'));
      var hi = parseFloat(container.getAttribute('data-max-bound'));
      var min, max;
      if (input.hasAttribute('data-jaiio-price-range')) {
        var ranges = container.querySelectorAll('[data-jaiio-price-range]');
        min = parseFloat(ranges[0].value);
        max = parseFloat(ranges[1].value);
        if (min > max) { var tmp = min; min = max; max = tmp; }
      } else {
        var inputs = container.querySelectorAll('[data-jaiio-price-input]');
        min = toNumber(inputs[0].value);
        max = toNumber(inputs[1].value);
        if (min == null) min = lo;
        if (max == null) max = hi;
        if (min > max) { var tmp2 = min; min = max; max = tmp2; }
      }
      state.prixMin = min > lo ? min : null;
      state.prixMax = max < hi ? max : null;
      applyChange({ action: 'price', key: 'prix', value: (state.prixMin || lo) + '-' + (state.prixMax || hi) });
    }

    root.addEventListener('click', function (event) {
      var firstPage = event.target.closest('[data-jaiio-first-page]');
      if (firstPage) {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        state.page = 1;
        state.take = PAGE_SIZE;
        writeUrl(false);
        scrollToResults();
        loadFresh();
        return;
      }
      var toggle = event.target.closest('[data-jaiio-facet-toggle]');
      if (toggle) {
        var key = toggle.getAttribute('data-jaiio-facet-toggle');
        openFacets[key] = !openFacets[key];
        toggle.setAttribute('aria-expanded', openFacets[key] ? 'true' : 'false');
        var body = toggle.parentElement.querySelector('.jaiio-facet__body');
        if (body) body.hidden = !openFacets[key];
        return;
      }
      var remove = event.target.closest('[data-jaiio-remove-filter]');
      if (remove) {
        var rKey = remove.getAttribute('data-jaiio-remove-filter');
        if (rKey === 'prix') {
          state.prixMin = null;
          state.prixMax = null;
          applyChange({ action: 'remove', key: 'prix' });
        } else {
          toggleFilterValue(rKey, remove.getAttribute('data-jaiio-remove-value'), false);
        }
        return;
      }
      if (event.target.closest('[data-jaiio-clear-all]')) {
        clearAllFilters();
        return;
      }
      if (event.target.closest('[data-jaiio-retry]')) {
        setUnavailable(false);
        if (usesShopifyPrice() && nativeMoreFailed) { loadMore(false); return; }
        loadFresh({ silent: !!els.grid.children.length });
        return;
      }
      if (event.target.closest('[data-jaiio-open-facets]')) {
        openFacetsPanel(true);
        return;
      }
      if (event.target.closest('[data-jaiio-close-facets]')) {
        openFacetsPanel(false);
        return;
      }
      var sortButton = event.target.closest('[data-jaiio-sort-button]');
      if (sortButton) {
        openSortPopover(!els.sortPopover.hasAttribute('open'));
        return;
      }
      if (event.target.closest('[data-jaiio-sort-close]')) {
        openSortPopover(false);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      openSortPopover(false);
      openFacetsPanel(false);
    });

    // Navigation back/forward : l'URL est la source de vérité.
    window.addEventListener('popstate', function () {
      // Au retour BFCache, la bonne grille est déjà en mémoire. La vider ici
      // retire sa hauteur et ramène le navigateur en haut inutilement.
      if (renderedUrl === viewportUrl() && requestedUrl === viewportUrl()) { restoreViewport(); return; }
      parseUrl();
      renderSortChoices();
      loadFresh({ onDone: restoreViewport });
    });

    /* ── Boot ──────────────────────────────────────────────────────────── */

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    if (state.q) {
      dataLayerPush({ event: 'jaiio_search', search_term: state.q, page_type: config.page_type });
    }

    loadFresh({ onDone: restoreViewport, silent: ssrActive && pristineUrl });
  }
})();
